import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Select } from "antd";
import type {
  TCommunicationChangeAction,
  TCommunicationAuditEntry,
  TCommunicationContextGroup,
  TCommunicationDismissReason,
  TCommunicationEquipmentChange,
  TCommunicationQuestion,
  TCommunicationRequestAction,
  TCommunicationRequestGroup,
  TCommunicationRequestStatus,
  TCommunicationReferenceOption,
  TCommunicationSuggestionStatus,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import {
  buildCommunicationLogisticsPreview,
} from "../../helpers/communication-preview-helper";
import { formatCommunicationDateTime } from "../../helpers/communication-date-helper";
import { localizeCommunicationLookupValue } from "../../helpers/communication-localize-helper";
import { buildCommunicationOriginalEmailSections } from "../../helpers/communication-original-email-helper";
import {
  buildCommunicationTaskSteps,
  TCommunicationTaskStep,
  TCommunicationTaskStepIconKey,
} from "../../helpers/communication-step-helper";
import {
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../../helpers/communication-confidence-helper";
import CommunicationOriginalEmailField from "../../fields/communication-original-email-field";
import CommunicationConst from "../../communication-const";
import { Localize, localizeByKeyStatic } from "components/service";
import Icons from "components/icons";
import { useWindowSize } from "components/use-hooks";

const { TextArea } = Input;

const COMMUNICATION_DRAWER_BREAKPOINTS = {
  mobile: 767,
  tablet: 1199,
  laptop: 1519,
} as const;

type TCommunicationDrawerPresentationMode =
  | "desktop"
  | "laptop-condensed"
  | "tablet-condensed"
  | "mobile-simplified";

const getCommunicationDrawerPresentationMode = (
  viewportWidth: number
): TCommunicationDrawerPresentationMode => {
  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.mobile) {
    return "mobile-simplified";
  }

  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.tablet) {
    return "tablet-condensed";
  }

  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.laptop) {
    return "laptop-condensed";
  }

  return "desktop";
};

const getDefaultReadingPaneExpanded = (
  mode: TCommunicationDrawerPresentationMode,
  step: number
): boolean => {
  if (mode === "mobile-simplified") {
    return false;
  }

  if (mode === "tablet-condensed" || mode === "laptop-condensed") {
    return step === 1;
  }

  return true;
};

interface ICommunicationTaskFormProps {
  task: TCommunicationTask;
  canEditRaw: boolean;
  canApprove: boolean;
  isAuditView?: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntry?: TCommunicationAuditEntry;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onDismissNotRelevant?: (taskId: string, reason: TCommunicationDismissReason) => void;
  onDirtyStart?: (taskId: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  action?: (options?: { ignoreUnsavedChanges?: boolean }) => void;
  showModal?: boolean;
}

const actionOptions = [
  { value: "add", label: localizeByKeyStatic("COMMUNICATION.Action_Add") },
  { value: "remove", label: localizeByKeyStatic("COMMUNICATION.Action_Remove") },
  { value: "change", label: localizeByKeyStatic("COMMUNICATION.Action_Change") },
];

const actionLabelKeyMap: Record<TCommunicationChangeAction, string> = {
  add: "COMMUNICATION.Action_Add",
  remove: "COMMUNICATION.Action_Remove",
  change: "COMMUNICATION.Action_Change",
};

const requestActionLabelKeyMap: Record<TCommunicationRequestAction, string> = {
  ...actionLabelKeyMap,
  question: "COMMUNICATION.Action_Question",
};

const requestStatusLabelKeyMap: Record<TCommunicationRequestStatus, string> = {
  pending: "COMMUNICATION.RequestStatus_Pending",
  unidentified: "COMMUNICATION.RequestStatus_Unidentified",
  approved: "COMMUNICATION.RequestStatus_Approved",
  rejected: "COMMUNICATION.RequestStatus_Rejected",
  cancelled: "COMMUNICATION.RequestStatus_Cancelled",
};

const workStateLabelKeyMap: Record<TCommunicationTask["work_state"]["status"], string> = {
  untouched: "COMMUNICATION.WorkState_Untouched",
  in_progress: "COMMUNICATION.WorkState_InProgress",
  approved: "COMMUNICATION.WorkState_Approved",
};

const questionStatusOptions = [
  { value: "open", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open") },
  { value: "reviewed", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed") },
];

const questionStatusLabelMap: Record<TCommunicationQuestion["status"], string> = {
  open: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open"),
  reviewed: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed"),
};

const dismissReasonOptions: Array<{ value: TCommunicationDismissReason; label: string }> = [
  {
    value: "spam_marketing",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_spam_marketing"),
  },
  {
    value: "no_project_context",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_no_project_context"),
  },
  {
    value: "wrong_recipient",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_wrong_recipient"),
  },
  {
    value: "informational_only",
    label: localizeByKeyStatic("COMMUNICATION.NotRelevantReason_informational_only"),
  },
];

type TReplyDraftTemplateKey = "available" | "unavailable" | "under_review";

const suggestionStatusLabelKeyMap: Record<TCommunicationSuggestionStatus, string> = {
  pending: "COMMUNICATION.EquipmentSuggestion_Pending",
  confirmed: "COMMUNICATION.EquipmentSuggestion_Confirmed",
  changed: "COMMUNICATION.EquipmentSuggestion_Changed",
};

const createEmptyEquipmentChange = (taskId: string, standNumber?: string) => ({
  id: `new-${taskId}-${Date.now()}`,
  action: "add" as const,
  item_label: "",
  event_name: undefined,
  company_name: undefined,
  stand_number: standNumber,
  qty: 1,
  color: null,
  equipment_group_id: undefined,
  suggested_equipment_group_id: undefined,
  suggestion_status: undefined,
});

const buildContextGroupLookupKey = (
  eventName?: string | null,
  companyName?: string | null,
  standNumber?: string | null
): string => [ eventName?.trim() || "", companyName?.trim() || "", standNumber?.trim() || "" ].join("|");

const findMatchingContextGroup = (
  change: Partial<TCommunicationEquipmentChange> | null | undefined,
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup | undefined => {
  const eventName = typeof change?.event_name === "string" ? change.event_name.trim() : "";
  const companyName = typeof change?.company_name === "string" ? change.company_name.trim() : "";
  const standNumber = typeof change?.stand_number === "string" ? change.stand_number.trim() : "";

  return contextGroups.find((group) =>
    group.event_name.trim() === eventName
    && group.company_name.trim() === companyName
    && group.stand_number.trim() === standNumber
  )
    || contextGroups.find((group) =>
      group.stand_number.trim() === standNumber
      && (!eventName || group.event_name.trim() === eventName)
      && (!companyName || group.company_name.trim() === companyName)
    )
    || contextGroups.find((group) => group.stand_number.trim() === standNumber);
};

const isEquipmentChangeVisibleForContexts = (
  change: Partial<TCommunicationEquipmentChange> | null | undefined,
  contextGroups: TCommunicationContextGroup[]
): boolean => {
  if (!contextGroups.length) {
    return true;
  }

  const hasExplicitContext = Boolean(
    (typeof change?.event_name === "string" && change.event_name.trim())
    || (typeof change?.company_name === "string" && change.company_name.trim())
    || (typeof change?.stand_number === "string" && change.stand_number.trim())
  );

  if (!hasExplicitContext) {
    return contextGroups.length <= 1;
  }

  return Boolean(findMatchingContextGroup(change, contextGroups));
};

const getExcludedContextGroupsFromRequestGroups = (
  requestGroups: TCommunicationRequestGroup[],
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup[] => {
  const activeContextKeys = new Set(
    contextGroups.map((group) =>
      buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number)
    )
  );

  return requestGroups
    .map((group) => ({
      id: group.id,
      event_name: group.event_name,
      company_name: group.company_name,
      stand_number: group.stand_number,
    }))
    .filter((group) =>
      !activeContextKeys.has(buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number))
    );
};

const stripStepTitleIndex = (label: string): string =>
  label.replace(/^\s*\d+\.\s*/u, "").trim();

const stepIconMap: Record<TCommunicationTaskStepIconKey, React.ElementType> = {
  check: Icons.Check,
  attention: Icons.Attention,
  note: Icons.Note,
  lock: Icons.Padlock,
};

const getCountLabel = (
  count: number,
  singularKey: string,
  pluralKey: string
): string => localizeByKeyStatic(count === 1 ? singularKey : pluralKey, { count });

const getRequestStatusTone = (status: TCommunicationRequestStatus): string => {
  if (status === "approved") {
    return "approved";
  }

  if (status === "unidentified") {
    return "warning";
  }

  if (status === "rejected" || status === "cancelled") {
    return "muted";
  }

  return "pending";
};

type TValidationFieldName = string | (string | number)[];
type TValidateFieldsError = {
  errorFields?: Array<{
    name: (string | number)[];
  }>;
};

const normalizeStandNumbers = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean)
    )
  );

const normalizeContextGroups = (
  contextGroups: Array<Partial<TCommunicationContextGroup> | null | undefined>,
  taskId: string
): TCommunicationContextGroup[] => {
  const normalizedGroups = contextGroups
    .map((group, index) => ({
      id:
        typeof group?.id === "string" && group.id.trim()
          ? group.id.trim()
          : `context-${taskId}-${index}`,
      event_name: typeof group?.event_name === "string" ? group.event_name : "",
      company_name: typeof group?.company_name === "string" ? group.company_name : "",
      stand_number: typeof group?.stand_number === "string" ? group.stand_number : "",
    }))
    .filter((group) => group.event_name.trim() || group.company_name.trim() || group.stand_number.trim());

  return normalizedGroups.length > 0
    ? normalizedGroups
    : [{
      id: `context-${taskId}-0`,
      event_name: "",
      company_name: "",
      stand_number: "",
    }];
};

const getPrimaryContextGroup = (
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup | undefined =>
  contextGroups.find((group) =>
    group.event_name.trim() && group.company_name.trim() && group.stand_number.trim()
  ) || contextGroups[0];

const areContextGroupsComplete = (
  contextGroups: TCommunicationContextGroup[]
): boolean =>
  contextGroups.length > 0
  && contextGroups.every((group) =>
    Boolean(group.event_name.trim() && group.company_name.trim() && group.stand_number.trim())
  );

type TEditableChangeGroup = {
  key: string;
  standNumber?: string;
  eventName?: string;
  companyName?: string;
  itemIndexes: number[];
};

// Legacy helper kept only to avoid touching the previously encoded block in-place.
// New code should use formatCanonicalStandNumberLabel.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatStandNumberLabel = (value?: string | null): string => {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) {
    return "\u2014";
  }

  const withoutPrefix = normalizedValue.replace(/^(№\s*|N\s*|#\s*)/iu, "").trim();

  return `№ ${withoutPrefix || normalizedValue}`;
};

const formatStandGroupTitle = (value?: string | null): string => {
  const formattedStandNumber = formatCanonicalStandNumberLabel(value);

  if (formattedStandNumber === "\u2014") {
    return localizeByKeyStatic("COMMUNICATION.RequestGroup_StandFallback");
  }

  return `STAND ${formattedStandNumber}`;
};

const buildStandToneStyle = (index: number): React.CSSProperties => {
  const hue = Math.round((index * 137.508) % 360);

  return {
    "--communication-stand-accent": `hsl(${hue} 58% 42%)`,
    "--communication-stand-soft": `hsl(${hue} 86% 84% / 0.34)`,
    "--communication-stand-pill-bg": `hsl(${hue} 90% 95%)`,
    "--communication-stand-pill-border": `hsl(${hue} 50% 78%)`,
    "--communication-stand-pill-color": `hsl(${hue} 58% 38%)`,
  } as React.CSSProperties;
};

const buildNeutralStandToneStyle = (): React.CSSProperties =>
  ({
    "--communication-stand-accent": "hsl(214 34% 58%)",
    "--communication-stand-soft": "hsl(214 56% 92% / 0.52)",
    "--communication-stand-pill-bg": "hsl(214 50% 96%)",
    "--communication-stand-pill-border": "hsl(214 34% 84%)",
    "--communication-stand-pill-color": "hsl(214 34% 42%)",
  }) as React.CSSProperties;

const formatCanonicalStandNumberLabel = (value?: string | null): string => {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) {
    return "\u2014";
  }

  const withoutPrefix = normalizedValue.replace(/^(\u2116\s*|N\s*|#\s*)/iu, "").trim();

  return `\u2116 ${withoutPrefix || normalizedValue}`;
};

const getEditableChangeStandNumber = (
  change?: TCommunicationEquipmentChange | null
): string => (typeof change?.stand_number === "string" ? change.stand_number.trim() : "");

const hasStandUpdates = (standUpdates?: Partial<TCommunicationTaskFormValues["stand_updates"]> | null): boolean =>
  Boolean(
    (typeof standUpdates?.flooring_id === "string"
      ? standUpdates.flooring_id.trim()
      : standUpdates?.flooring_id)
    || (typeof standUpdates?.flooring_color === "string"
      ? standUpdates.flooring_color.trim()
      : standUpdates?.flooring_color)
  );

const hasManagerNote = (managerNote?: string | null): boolean =>
  Boolean(managerNote?.trim());

const resolveEquipmentGroupId = (
  equipmentGroupId: string | null | undefined,
  equipmentId: string | null | undefined,
  equipmentIdToGroupIdMap: Map<string, string>
): string | undefined => {
  if (equipmentGroupId?.trim()) {
    return equipmentGroupId;
  }

  if (equipmentId?.trim()) {
    return equipmentIdToGroupIdMap.get(equipmentId);
  }

  return undefined;
};

const buildMergedEquipmentOptions = (
  equipmentOptions: TCommunicationReferenceOption[],
  equipmentChanges: TCommunicationTask["final_changes"]["equipment_changes"],
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationReferenceOption[] => {
  const optionMap = new Map(
    equipmentOptions.map((option) => [ option.value, option ])
  );

  equipmentChanges.forEach((change) => {
    const resolvedGroupId = resolveEquipmentGroupId(
      change.equipment_group_id || change.suggested_equipment_group_id,
      change.equipment_id,
      equipmentIdToGroupIdMap
    );

    if (!resolvedGroupId || optionMap.has(resolvedGroupId)) {
      return;
    }

    optionMap.set(resolvedGroupId, {
      value: resolvedGroupId,
      label: change.equipment_title || change.item_label || resolvedGroupId,
      rawId: change.equipment_id,
    });
  });

  return Array.from(optionMap.values());
};

type TEquipmentSuggestionMeta = {
  suggestedGroupId: string;
  status: TCommunicationSuggestionStatus;
};

const getEquipmentSuggestionMeta = (
  change: TCommunicationTaskFormValues["equipment_changes"][number]
): TEquipmentSuggestionMeta | null => {
  if (!change?.suggested_equipment_group_id) {
    return null;
  }

  return {
    suggestedGroupId: change.suggested_equipment_group_id,
    status: change.suggestion_status || "pending",
  };
};

const getEffectiveEquipmentGroupId = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): string | undefined =>
  change?.equipment_group_id
  || (change?.suggestion_status !== "changed" ? change?.suggested_equipment_group_id || undefined : undefined);

const isBlankEquipmentChange = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): boolean =>
  (change?.action || "add") === "add"
  && (typeof change?.qty === "number" ? change.qty : 1) === 1
  && !getEffectiveEquipmentGroupId(change)
  && !change?.equipment_id?.trim()
  && !change?.item_label?.trim()
  && !change?.equipment_title?.trim()
  && !change?.color?.trim();

const shouldIgnoreEquipmentChangeValidation = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined,
  index: number,
  changes: TCommunicationTaskFormValues["equipment_changes"],
  hasExistingEquipmentChanges: boolean
): boolean =>
  !hasExistingEquipmentChanges
  && changes.length === 1
  && index === 0
  && isBlankEquipmentChange(change);

const getEquipmentValidationMessage = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined
): string => {
  const sourceLabel =
    change?.equipment_title?.trim()
    || change?.item_label?.trim()
    || "";

  if (sourceLabel) {
    return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentForItemOrDelete", {
      item: sourceLabel,
    });
  }

  return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentOrDelete");
};

const mapTaskToFormValues = (
  task: TCommunicationTask,
  equipmentIdToGroupIdMap: Map<string, string> = new Map()
): TCommunicationTaskFormValues => ({
  event_name: task.event_name || "",
  company_name: task.company_name || "",
  stand_number: task.stand_number || "",
  stand_numbers:
    task.stand_numbers.length > 0
      ? task.stand_numbers
      : task.stand_number
        ? [ task.stand_number ]
        : [ "" ],
  context_groups: normalizeContextGroups(
    task.context_groups?.length
      ? task.context_groups
      : [{
        id: `context-${task.id}-0`,
        event_name: task.event_name || "",
        company_name: task.company_name || "",
        stand_number: task.stand_number || task.stand_numbers[0] || "",
      }],
    task.id
  ),
  organizer_id: task.organizer_id || undefined,
  exhibition_type_id: task.exhibition_type_id || undefined,
  manager_note: task.final_changes.manager_note || "",
  reply_handoff_note: task.final_changes.reply_handoff_note || "",
  equipment_changes:
    task.final_changes.equipment_changes.length > 0
      ? task.final_changes.equipment_changes.map((change) => {
        const matchedContextGroup = findMatchingContextGroup(change, task.context_groups || []);
        const suggestedEquipmentGroupId = resolveEquipmentGroupId(
          change.suggested_equipment_group_id || change.equipment_group_id,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );
        const resolvedEquipmentGroupId = resolveEquipmentGroupId(
          change.equipment_group_id || suggestedEquipmentGroupId,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );

        return {
          ...change,
          event_name: change.event_name || matchedContextGroup?.event_name || task.event_name || "",
          company_name: change.company_name || matchedContextGroup?.company_name || task.company_name || "",
          stand_number: change.stand_number || matchedContextGroup?.stand_number || task.stand_number || "",
          color: localizeCommunicationLookupValue(change.color) || null,
          equipment_group_id: resolvedEquipmentGroupId,
          suggested_equipment_group_id: suggestedEquipmentGroupId,
          suggestion_status:
            suggestedEquipmentGroupId
              ? change.suggestion_status || "pending"
              : change.suggestion_status || undefined,
        };
      })
      : [createEmptyEquipmentChange(task.id, task.stand_number || undefined)],
  stand_updates: {
    flooring_id: task.final_changes.stand_updates.flooring_id || undefined,
    flooring_color:
      localizeCommunicationLookupValue(task.final_changes.stand_updates.flooring_color) || "",
  },
  questions: task.questions.length > 0 ? task.questions : [],
});

const getInitialStep = (task: TCommunicationTask): number => {
  if (
    task.status === CommunicationConst.statuses.APPROVED
    || task.status === CommunicationConst.statuses.DISMISSED
  ) {
    return 3;
  }

  if (task.context_groups?.length) {
    return areContextGroupsComplete(task.context_groups) ? 2 : 1;
  }

  const hasStandMatch = Boolean(
    task.stand_number?.trim() || normalizeStandNumbers(task.stand_numbers).length
  );

  return task.event_name?.trim() && task.company_name?.trim() && hasStandMatch ? 2 : 1;
};

const getDrawerStartStep = (
  task: TCommunicationTask,
  isAuditView?: boolean
): number => {
  if (isAuditView) {
    return getInitialStep(task);
  }

  return 1;
};

const getInitialUnlockedStep = (
  task: TCommunicationTask,
  isAuditView?: boolean
): TCommunicationTaskStep["id"] => {
  if (isAuditView || task.status === CommunicationConst.statuses.APPROVED) {
    return 3;
  }

  return getInitialStep(task) as TCommunicationTaskStep["id"];
};

const buildFormValueSnapshot = (
  task: TCommunicationTask,
  rawValues: Partial<TCommunicationTaskFormValues>,
  resolvedValues: {
    event_name: string;
    company_name: string;
    stand_number: string;
    stand_numbers: string[];
    context_groups: TCommunicationContextGroup[];
    equipment_changes: TCommunicationTaskFormValues["equipment_changes"];
    stand_updates: TCommunicationTaskFormValues["stand_updates"];
    questions: TCommunicationTaskFormValues["questions"];
  }
): TCommunicationTaskFormValues => ({
  event_name: resolvedValues.event_name,
  company_name: resolvedValues.company_name,
  stand_number: resolvedValues.stand_number,
  stand_numbers: resolvedValues.stand_numbers,
  context_groups: resolvedValues.context_groups,
  organizer_id: rawValues.organizer_id || task.organizer_id || undefined,
  exhibition_type_id: rawValues.exhibition_type_id || task.exhibition_type_id || undefined,
  manager_note:
    typeof rawValues.manager_note === "string"
      ? rawValues.manager_note
      : task.final_changes.manager_note || "",
  reply_handoff_note:
    typeof rawValues.reply_handoff_note === "string"
      ? rawValues.reply_handoff_note
      : task.final_changes.reply_handoff_note || "",
  equipment_changes: resolvedValues.equipment_changes,
  stand_updates: resolvedValues.stand_updates,
  questions: resolvedValues.questions,
});

const normalizeTaskFormSnapshot = (
  task: TCommunicationTask,
  rawValues: Partial<TCommunicationTaskFormValues>,
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationTaskFormValues => {
  const fallbackValues = mapTaskToFormValues(task, equipmentIdToGroupIdMap);
  const sourceStandNumbers =
    Array.isArray(rawValues.stand_numbers) && rawValues.stand_numbers.length > 0
      ? rawValues.stand_numbers.map((value) => (typeof value === "string" ? value : ""))
      : fallbackValues.stand_numbers;
  const normalizedStandNumbers = normalizeStandNumbers(sourceStandNumbers);
  const primaryStandNumber =
    normalizedStandNumbers[0]
    || (typeof rawValues.stand_number === "string" ? rawValues.stand_number.trim() : "")
    || fallbackValues.stand_number
    || "";
  const normalizedContextGroups = normalizeContextGroups(
    Array.isArray(rawValues.context_groups) && rawValues.context_groups.length > 0
      ? rawValues.context_groups
      : fallbackValues.context_groups,
    task.id
  );
  const primaryContextGroup = getPrimaryContextGroup(normalizedContextGroups);

  return buildFormValueSnapshot(task, rawValues, {
    event_name: primaryContextGroup?.event_name || fallbackValues.event_name,
    company_name: primaryContextGroup?.company_name || fallbackValues.company_name,
    stand_number: primaryContextGroup?.stand_number || primaryStandNumber,
    stand_numbers: normalizeStandNumbers([
      ...normalizedContextGroups.map((group) => group.stand_number),
      ...normalizedStandNumbers,
    ]),
    context_groups: normalizedContextGroups,
    equipment_changes: rawValues.equipment_changes || fallbackValues.equipment_changes,
    stand_updates: rawValues.stand_updates || fallbackValues.stand_updates,
    questions: rawValues.questions || fallbackValues.questions,
  });
};

const CommunicationTaskForm: React.FC<ICommunicationTaskFormProps> = ({
  task,
  canApprove,
  equipmentOptions,
  flooringOptions,
  organizerOptions,
  exhibitionOptions,
  auditEntry,
  isAuditView,
  onApprove,
  onDismissNotRelevant,
  onDirtyStart,
  onDirtyChange,
  action,
  showModal,
}) => {
  const [form] = Form.useForm<TCommunicationTaskFormValues>();
  const viewportWidth = useWindowSize()?.width || 0;
  const presentationMode = useMemo(
    () => getCommunicationDrawerPresentationMode(viewportWidth),
    [viewportWidth]
  );
  const isMobileSimplified = presentationMode === "mobile-simplified";
  const isTabletCondensed = presentationMode === "tablet-condensed";
  const shouldShowStepDescriptions = !isMobileSimplified && !isTabletCondensed;
  const shouldRenderSecondaryContextRibbon = !isMobileSimplified;
  const shouldRenderDemoStrip = !isMobileSimplified;
  const shouldRenderStepOneSummary = !isMobileSimplified;
  const shouldRenderStepOneFollowUpStrip = !isMobileSimplified;
  const shouldRenderStepTwoSummary = !isMobileSimplified;
  const shouldRenderExtractedLogisticsBand = !isMobileSimplified;
  const [isDirty, setIsDirty] = useState(false);
  const hasTriggeredDirtyStartRef = useRef(false);
  const readingPaneWheelDirectionRef = useRef<"up" | "down" | null>(null);
  const canAutoExpandReadingPaneRef = useRef(false);
  const isReadingPaneScrollCondensedRef = useRef(false);
  const [currentStep, setCurrentStep] = useState<number>(getDrawerStartStep(task, isAuditView));
  const [maxUnlockedStep, setMaxUnlockedStep] = useState<TCommunicationTaskStep["id"]>(
    getInitialUnlockedStep(task, isAuditView)
  );
  const [openEquipmentField, setOpenEquipmentField] = useState<number | null>(null);
  const [isReadingPaneExpanded, setIsReadingPaneExpanded] = useState<boolean>(
    () => getDefaultReadingPaneExpanded(presentationMode, getDrawerStartStep(task, isAuditView))
  );
  const [isReadingPaneScrollCondensed, setIsReadingPaneScrollCondensed] = useState<boolean>(false);
  const [showStandUpdates, setShowStandUpdates] = useState<boolean>(
    hasStandUpdates(task.extracted_changes.stand_updates) || hasStandUpdates(task.final_changes.stand_updates)
  );
  const [showManagerNote, setShowManagerNote] = useState<boolean>(
    hasManagerNote(task.final_changes.manager_note)
  );
  const [showReplyHandoffNote, setShowReplyHandoffNote] = useState<boolean>(
    Boolean(task.final_changes.reply_handoff_note?.trim()) || task.questions.length > 0
  );
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<TCommunicationDismissReason>("no_project_context");
  const shouldAutoCondenseReadingPane = isReadingPaneExpanded && currentStep === 2;
  const confidenceValue = normalizeCommunicationConfidence(task.confidence);
  const confidenceTone = getCommunicationConfidenceTone(confidenceValue);
  const equipmentIdToGroupIdMap = useMemo(
    () =>
      new Map(
        equipmentOptions.flatMap((option) => {
          const pairs: Array<[string, string]> = [[ option.value, option.value ]];

          if (option.rawId) {
            pairs.push([ option.rawId as string, option.value ]);
          }

          return pairs;
        })
      ),
    [equipmentOptions]
  );
  const initialFormValues = useMemo(
    () => mapTaskToFormValues(task, equipmentIdToGroupIdMap),
    [
      equipmentIdToGroupIdMap,
      task.id,
      task.event_name,
      task.company_name,
      task.stand_number,
      task.organizer_id,
      task.exhibition_type_id,
      task.final_changes,
      task.questions,
      task.stand_numbers,
    ]
  );
  const initialFormSnapshot = useMemo(
    () =>
      JSON.stringify(
        normalizeTaskFormSnapshot(task, initialFormValues, equipmentIdToGroupIdMap)
      ),
    [equipmentIdToGroupIdMap, initialFormValues, task]
  );

  const watchedQuestions = Form.useWatch("questions", form);
  const watchedContextGroups = Form.useWatch("context_groups", form);
  const watchedEventName = Form.useWatch("event_name", form);
  const watchedCompanyName = Form.useWatch("company_name", form);
  const watchedStandNumber = Form.useWatch("stand_number", form);
  const watchedStandNumbers = Form.useWatch("stand_numbers", form);
  const watchedEquipmentChanges = Form.useWatch("equipment_changes", form);
  const watchedStandUpdates = Form.useWatch("stand_updates", form);
  const watchedReplyHandoffNote = Form.useWatch("reply_handoff_note", form);
  const resolvedQuestions =
    watchedQuestions ?? form.getFieldValue("questions") ?? task.questions;
  const resolvedContextGroups = useMemo(
    () => normalizeContextGroups(
      watchedContextGroups
      ?? form.getFieldValue("context_groups")
      ?? task.context_groups
      ?? [{
        id: `context-${task.id}-0`,
        event_name: task.event_name || "",
        company_name: task.company_name || "",
        stand_number: task.stand_number || "",
      }],
      task.id
    ),
    [form, task.company_name, task.context_groups, task.event_name, task.id, task.stand_number, watchedContextGroups]
  );
  const primaryContextGroup = getPrimaryContextGroup(resolvedContextGroups);
  const resolvedEventName =
    primaryContextGroup?.event_name
    || watchedEventName
    || form.getFieldValue("event_name")
    || task.event_name
    || "";
  const resolvedCompanyName =
    primaryContextGroup?.company_name
    || watchedCompanyName
    || form.getFieldValue("company_name")
    || task.company_name
    || "";
  const resolvedStandNumber =
    primaryContextGroup?.stand_number
    || watchedStandNumber
    || form.getFieldValue("stand_number")
    || task.stand_number
    || "";
  const resolvedStandNumbers = useMemo(() => {
    const sourceStandNumbers =
      resolvedContextGroups.length > 0
      ? resolvedContextGroups.map((group) => group.stand_number)
      : watchedStandNumbers
      ?? form.getFieldValue("stand_numbers")
      ?? task.stand_numbers
      ?? (task.stand_number ? [ task.stand_number ] : [ "" ]);

    return Array.isArray(sourceStandNumbers) && sourceStandNumbers.length > 0
      ? sourceStandNumbers.map((value) => (typeof value === "string" ? value : ""))
      : [ "" ];
  }, [form, resolvedContextGroups, task.stand_number, task.stand_numbers, watchedStandNumbers]);
  const normalizedStandNumbers = useMemo(
    () => normalizeStandNumbers(resolvedStandNumbers),
    [resolvedStandNumbers]
  );
  const primaryStandNumber =
    primaryContextGroup?.stand_number || normalizedStandNumbers[0] || resolvedStandNumber || task.stand_number || "";
  const hasBlankStandNumbers = resolvedContextGroups.some((group) => !group.stand_number?.trim())
    || resolvedStandNumbers.some((value) => !value?.trim());
  const sourceEquipmentChanges = useMemo(
    () =>
      watchedEquipmentChanges
      ?? form.getFieldValue("equipment_changes")
      ?? task.final_changes.equipment_changes,
    [form, task.final_changes.equipment_changes, watchedEquipmentChanges]
  );
  const visibleEquipmentChangeEntries = useMemo(
    () =>
      (sourceEquipmentChanges || [])
        .map((change, index) => ({ change, index }))
        .filter(({ change }) => isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)),
    [resolvedContextGroups, sourceEquipmentChanges]
  );
  const resolvedEquipmentChanges = useMemo(
    () => visibleEquipmentChangeEntries.map(({ change }) => change),
    [visibleEquipmentChangeEntries]
  );
  const resolvedStandUpdates =
    watchedStandUpdates
    ?? form.getFieldValue("stand_updates")
    ?? task.final_changes.stand_updates
    ?? {};
  const resolvedReplyHandoffNote =
    watchedReplyHandoffNote
    ?? form.getFieldValue("reply_handoff_note")
    ?? task.final_changes.reply_handoff_note
    ?? "";
  const isFinalizedTask = Boolean(isAuditView || task.status === CommunicationConst.statuses.APPROVED);
  const isDismissedHistoryEntry = Boolean(isAuditView && auditEntry?.outcome === "dismissed");
  const isAiFallbackTask = task.request_source === "ai_fallback";
  const requestGroups = task.request_groups;
  const requestItemsCount = task.request_items.length;
  const workStateLabel = localizeByKeyStatic(workStateLabelKeyMap[task.work_state.status]);
  const dismissedReasonLabel =
    isDismissedHistoryEntry && auditEntry?.dismissed_reason
      ? localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${auditEntry.dismissed_reason}`)
      : "";
  const workStateTouchedLabel = task.work_state.touched_at
    ? localizeByKeyStatic("COMMUNICATION.WorkState_LastTouched", {
      date: formatCommunicationDateTime(task.work_state.touched_at),
    })
    : "";
  const hasExistingEquipmentChanges = Boolean(
    task.extracted_changes.equipment_changes.length || task.final_changes.equipment_changes.length
  );
  const hasSpamSignal = task.intent === "spam";
  const requestGroupsHintKey = isAiFallbackTask
    ? "COMMUNICATION.Section_RequestGroupsHintFallback"
    : "COMMUNICATION.Section_RequestGroupsHint";
  const extractedChangesHintKey = isAiFallbackTask
    ? "COMMUNICATION.Preview_LogisticsExtractedHintFallback"
    : "COMMUNICATION.Preview_LogisticsExtractedHint";
  const openQuestionsCount = resolvedQuestions.filter((question) => question?.status === "open").length;
  const pendingEquipmentSuggestionsCount = resolvedEquipmentChanges.filter((change) => {
    const suggestionMeta = getEquipmentSuggestionMeta(change);
    return suggestionMeta?.status === "pending";
  }).length;
  const unresolvedEquipmentSelectionCount = resolvedEquipmentChanges.filter((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change);
  }).length;
  const hasStepTwoBlockingIssues = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !getEffectiveEquipmentGroupId(change) || !change?.qty || change.qty < 1;
  });
  const hasReviewableEquipmentChanges = resolvedEquipmentChanges.some((change, index, changes) => {
    if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
      return false;
    }

    return !isBlankEquipmentChange(change);
  });
  const reviewChangesCount = pendingEquipmentSuggestionsCount + unresolvedEquipmentSelectionCount;
  const stepTwoReviewCount = reviewChangesCount + openQuestionsCount;
  const reviewedQuestionsCount = resolvedQuestions.filter((question) => question?.status === "reviewed").length;
  const isMatchComplete = areContextGroupsComplete(resolvedContextGroups);
  const hasDismissReviewSignals = Boolean(
    requestItemsCount > 0
    || hasReviewableEquipmentChanges
    || resolvedQuestions.length > 0
  );
  const shouldShowMarkAsNotRelevant = Boolean(
    currentStep === 1
    && !isAuditView
    && onDismissNotRelevant
    && !isMatchComplete
  );
  const matchingMissingFieldLabels = useMemo(() => {
    const missingFields: string[] = [];

    if (resolvedContextGroups.some((group) => !group.event_name?.trim())) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_EventName"));
    }

    if (resolvedContextGroups.some((group) => !group.company_name?.trim())) {
      missingFields.push(localizeByKeyStatic("FORMS.Input_Label_CompanyName"));
    }

    if (resolvedContextGroups.some((group) => !group.stand_number?.trim()) || normalizedStandNumbers.length === 0 || hasBlankStandNumbers) {
      missingFields.push(localizeByKeyStatic("COMMUNICATION.Field_StandNumbers"));
    }

    return missingFields;
  }, [
    resolvedContextGroups,
    hasBlankStandNumbers,
    normalizedStandNumbers.length,
  ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    hasTriggeredDirtyStartRef.current = false;
    setIsDirty(false);
    onDirtyChange?.(false);
    form.resetFields();
    form.setFieldsValue(initialFormValues);
    const startStep = getDrawerStartStep(task, isAuditView);
    setCurrentStep(startStep);
    setMaxUnlockedStep(getInitialUnlockedStep(task, isAuditView));
    setOpenEquipmentField(null);
    readingPaneWheelDirectionRef.current = null;
    canAutoExpandReadingPaneRef.current = false;
    isReadingPaneScrollCondensedRef.current = false;
    setIsReadingPaneExpanded(getDefaultReadingPaneExpanded(presentationMode, startStep));
    setIsReadingPaneScrollCondensed(false);
    setShowStandUpdates(
      hasStandUpdates(task.extracted_changes.stand_updates)
      || hasStandUpdates(task.final_changes.stand_updates)
    );
    setShowManagerNote(hasManagerNote(task.final_changes.manager_note));
    setShowReplyHandoffNote(
      Boolean(task.final_changes.reply_handoff_note?.trim()) || task.questions.length > 0
    );
  }, [ form, initialFormValues, isAuditView, onDirtyChange, presentationMode, showModal, task.id ]);

  useEffect(() => {
    if (!showModal || typeof document === "undefined" || !shouldAutoCondenseReadingPane) {
      setIsReadingPaneScrollCondensed(false);
      return undefined;
    }

    const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;

    if (!drawerBody) {
      setIsReadingPaneScrollCondensed(false);
      return undefined;
    }

    const condenseThreshold = 56;
    const expandThreshold = 4;
    const handleWheelIntent = (event: WheelEvent) => {
      if (event.deltaY > 0) {
        readingPaneWheelDirectionRef.current = "down";
        canAutoExpandReadingPaneRef.current = false;
        return;
      }

      if (event.deltaY < 0) {
        readingPaneWheelDirectionRef.current = "up";

        if (isReadingPaneScrollCondensedRef.current) {
          canAutoExpandReadingPaneRef.current = true;
        }
      }
    };

    const handleDrawerScroll = () => {
      const nextScrollTop = drawerBody.scrollTop;

      setIsReadingPaneScrollCondensed((currentValue) => {
        if (nextScrollTop >= condenseThreshold) {
          if (!currentValue) {
            canAutoExpandReadingPaneRef.current = false;
          }

          isReadingPaneScrollCondensedRef.current = true;
          return true;
        }

        if (
          currentValue
          && nextScrollTop <= expandThreshold
          && canAutoExpandReadingPaneRef.current
          && readingPaneWheelDirectionRef.current === "up"
        ) {
          canAutoExpandReadingPaneRef.current = false;
          isReadingPaneScrollCondensedRef.current = false;
          return false;
        }

        isReadingPaneScrollCondensedRef.current = currentValue;
        return currentValue;
      });
    };

    handleDrawerScroll();
    drawerBody.addEventListener("wheel", handleWheelIntent, { passive: true });
    drawerBody.addEventListener("scroll", handleDrawerScroll, { passive: true });

    return () => {
      drawerBody.removeEventListener("wheel", handleWheelIntent);
      drawerBody.removeEventListener("scroll", handleDrawerScroll);
    };
  }, [showModal, shouldAutoCondenseReadingPane, task.id]);

  useEffect(() => {
    if (!showModal || typeof document === "undefined") {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;

      if (!drawerBody) {
        return;
      }

      drawerBody.scrollTo({ top: 0, behavior: "auto" });
      readingPaneWheelDirectionRef.current = null;
      canAutoExpandReadingPaneRef.current = false;
      isReadingPaneScrollCondensedRef.current = false;
      setIsReadingPaneScrollCondensed(false);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentStep, showModal, task.id]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    readingPaneWheelDirectionRef.current = null;
    canAutoExpandReadingPaneRef.current = false;
    isReadingPaneScrollCondensedRef.current = false;
    setIsReadingPaneExpanded(getDefaultReadingPaneExpanded(presentationMode, currentStep));
    setIsReadingPaneScrollCondensed(false);
  }, [ currentStep, presentationMode, showModal ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    form.setFieldsValue({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      stand_numbers: normalizedStandNumbers.length > 0 ? normalizedStandNumbers : [ "" ],
    });
  }, [
    form,
    normalizedStandNumbers,
    primaryStandNumber,
    resolvedCompanyName,
    resolvedEventName,
    showModal,
  ]);

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    const currentPrimaryStand = form.getFieldValue("stand_number") || "";

    if (currentPrimaryStand !== primaryStandNumber) {
      form.setFieldValue("stand_number", primaryStandNumber);
    }
  }, [form, primaryStandNumber, showModal]);

  useEffect(() => {
    if (isFinalizedTask) {
      if (maxUnlockedStep !== 3) {
        setMaxUnlockedStep(3);
      }

      return;
    }

    let nextUnlockedStep = maxUnlockedStep;

    if (!isMatchComplete) {
      nextUnlockedStep = 1;
    } else if (hasStepTwoBlockingIssues && nextUnlockedStep > 2) {
      nextUnlockedStep = 2;
    }

    if (nextUnlockedStep !== maxUnlockedStep) {
      setMaxUnlockedStep(nextUnlockedStep);
    }

    if (currentStep > nextUnlockedStep) {
      setCurrentStep(nextUnlockedStep);
    }
  }, [
    currentStep,
    hasStepTwoBlockingIssues,
    isFinalizedTask,
    isMatchComplete,
    maxUnlockedStep,
  ]);

  useEffect(() => {
    if (showModal === false || !equipmentIdToGroupIdMap.size) {
      return;
    }

    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasHydratedEquipmentMetadata = false;

    const hydratedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change?.equipment_id) {
        const nextSuggestedGroupId = change?.suggested_equipment_group_id;
        const nextEquipmentGroupId =
          change?.equipment_group_id
          || (change?.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

        if (
          nextEquipmentGroupId === change?.equipment_group_id
          && nextSuggestedGroupId === change?.suggested_equipment_group_id
          && change?.suggestion_status
        ) {
          return change;
        }

        hasHydratedEquipmentMetadata = true;

        return {
          ...change,
          equipment_group_id: nextEquipmentGroupId,
          suggested_equipment_group_id: nextSuggestedGroupId,
          suggestion_status:
            nextSuggestedGroupId
              ? change?.suggestion_status || "pending"
              : change?.suggestion_status,
        };
      }

      const resolvedGroupId = equipmentIdToGroupIdMap.get(change.equipment_id);
      const nextSuggestedGroupId =
        change.suggested_equipment_group_id || resolvedGroupId;
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (change.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

      if (!nextEquipmentGroupId && !nextSuggestedGroupId) {
        return change;
      }

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextSuggestedGroupId === change.suggested_equipment_group_id
        && change.suggestion_status
      ) {
        return change;
      }

      hasHydratedEquipmentMetadata = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        suggested_equipment_group_id: nextSuggestedGroupId,
        suggestion_status:
          nextSuggestedGroupId
            ? change.suggestion_status || "pending"
            : change.suggestion_status,
      };
    });

    if (hasHydratedEquipmentMetadata) {
      form.setFieldValue("equipment_changes", hydratedChanges);
    }
  }, [equipmentIdToGroupIdMap, form, showModal]);

  useEffect(() => {
    const nextMergedEquipmentOptions = buildMergedEquipmentOptions(
      equipmentOptions,
      task.final_changes.equipment_changes,
      equipmentIdToGroupIdMap
    );

    if (showModal === false || !nextMergedEquipmentOptions.length) {
      return;
    }

    const optionMap = new Map(
      nextMergedEquipmentOptions.map((option) => [ option.value, option.label ])
    );
    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasPatchedSuggestions = false;

    const patchedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change) {
        return change;
      }

      const shouldAutofillSuggestion = change.suggestion_status !== "changed";
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (shouldAutofillSuggestion ? change.suggested_equipment_group_id : undefined);
      const nextItemLabel =
        change.item_label || (nextEquipmentGroupId ? optionMap.get(nextEquipmentGroupId) || "" : "");

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextItemLabel === change.item_label
      ) {
        return change;
      }

      hasPatchedSuggestions = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        item_label: nextItemLabel,
      };
    });

    if (hasPatchedSuggestions) {
      form.setFieldValue("equipment_changes", patchedChanges);
    }
  }, [equipmentIdToGroupIdMap, equipmentOptions, form, showModal, task.final_changes.equipment_changes]);

  const steps = useMemo(
    () =>
      buildCommunicationTaskSteps({
        hasReviewableChanges: hasReviewableEquipmentChanges,
        isFinalized: isFinalizedTask,
        isMatchComplete,
        maxUnlockedStep,
        stepTwoReviewCount,
        openQuestionsCount,
      }),
    [
      hasReviewableEquipmentChanges,
      isFinalizedTask,
      isMatchComplete,
      maxUnlockedStep,
      openQuestionsCount,
      stepTwoReviewCount,
    ]
  );

  const displayRequestGroups = useMemo(
    () => {
      const contextGroupMap = new Map(
        resolvedContextGroups.map((group) => [ group.id, group ])
      );

      return requestGroups.flatMap((group) => {
        const matchedContextGroup =
          contextGroupMap.get(group.id)
          || resolvedContextGroups.find((contextGroup) =>
            contextGroup.stand_number.trim() === group.stand_number.trim()
            && contextGroup.company_name.trim() === group.company_name.trim()
            && contextGroup.event_name.trim() === group.event_name.trim()
          );

        if (!matchedContextGroup) {
          return [];
        }

        return {
          ...group,
          stand_number: matchedContextGroup?.stand_number?.trim() || group.stand_number,
          company_name: matchedContextGroup?.company_name?.trim() || group.company_name,
          event_name: matchedContextGroup?.event_name?.trim() || group.event_name,
        };
      });
    },
    [requestGroups, resolvedContextGroups]
  );
  const visibleRequestItemsCount = useMemo(
    () => displayRequestGroups.reduce((sum, group) => sum + group.requests.length, 0),
    [displayRequestGroups]
  );
  const requestGroupCount = displayRequestGroups.length;
  const hasMultipleRequestGroups = requestGroupCount > 1;
  const excludedContextGroups = useMemo(
    () => getExcludedContextGroupsFromRequestGroups(task.request_groups, resolvedContextGroups),
    [resolvedContextGroups, task.request_groups]
  );
  const followUpRequestGroup =
    displayRequestGroups.find((group) => group.question_count > 0)
    || displayRequestGroups.find((group) => group.unidentified_count > 0)
    || displayRequestGroups[0];
  const followUpStandNumber = followUpRequestGroup?.stand_number || primaryStandNumber || "";

  const mergedEquipmentOptions = useMemo(
    () =>
      buildMergedEquipmentOptions(
        equipmentOptions,
        task.final_changes.equipment_changes,
        equipmentIdToGroupIdMap
      ),
    [ equipmentIdToGroupIdMap, equipmentOptions, task.final_changes.equipment_changes ]
  );

  const equipmentOptionsMap = useMemo(
    () => new Map(mergedEquipmentOptions.map((option) => [ option.value, option.label ])),
    [mergedEquipmentOptions]
  );

  const equipmentSelectOptions = useMemo(
    () =>
      mergedEquipmentOptions.map((option) => ({
        ...option,
        searchLabel: option.label,
      })),
    [mergedEquipmentOptions]
  );
  const standSelectOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...resolvedStandNumbers,
            ...resolvedContextGroups.map((group) => group.stand_number).filter(Boolean),
            ...displayRequestGroups.map((group) => group.stand_number).filter(Boolean),
            ...resolvedEquipmentChanges.map((change) => change?.stand_number || "").filter(Boolean),
            task.stand_number || "",
          ].filter(Boolean)
        )
      ).map((standNumber) => ({
        value: standNumber,
        label: standNumber,
      })),
    [
      resolvedEquipmentChanges,
      displayRequestGroups,
      resolvedContextGroups,
      resolvedStandNumbers,
      task.stand_number,
    ]
  );
  const editableChangeGroups = useMemo<TEditableChangeGroup[]>(() => {
    const orderMap = new Map(
      standSelectOptions.map((option, index) => [ option.value.trim(), index ])
    );
    const groupsMap = new Map<string, TEditableChangeGroup>();

    visibleEquipmentChangeEntries.forEach(({ change, index }) => {
      const standNumber = getEditableChangeStandNumber(change);
      const matchedContextGroup = findMatchingContextGroup(change, resolvedContextGroups);
      const eventName =
        matchedContextGroup?.event_name
        || (typeof change?.event_name === "string" ? change.event_name.trim() : "")
        || undefined;
      const companyName =
        matchedContextGroup?.company_name
        || (typeof change?.company_name === "string" ? change.company_name.trim() : "")
        || undefined;
      const groupKey =
        matchedContextGroup?.id
        || buildContextGroupLookupKey(eventName, companyName, standNumber)
        || "__unassigned__";
      const existingGroup = groupsMap.get(groupKey);

      if (existingGroup) {
        existingGroup.itemIndexes.push(index);
        return;
      }

      groupsMap.set(groupKey, {
        key: groupKey,
        standNumber: standNumber || undefined,
        eventName,
        companyName,
        itemIndexes: [ index ],
      });
    });

    return Array.from(groupsMap.values()).sort((left, right) => {
      const leftOrder = left.standNumber ? orderMap.get(left.standNumber) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      const rightOrder = right.standNumber ? orderMap.get(right.standNumber) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return (left.standNumber || "").localeCompare(right.standNumber || "");
    });
  }, [ resolvedContextGroups, standSelectOptions, visibleEquipmentChangeEntries ]);
  const hasMultiStandEditing = resolvedContextGroups.length > 1 || editableChangeGroups.length > 1 || standSelectOptions.length > 1;

  const finalPreviewEquipmentChanges = useMemo(() => {
    const watchedChanges = watchedEquipmentChanges || [];
    const formChanges =
      (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
    const sourceChanges =
      watchedChanges.length
        ? watchedChanges
        : formChanges.length
          ? formChanges
          : task.final_changes.equipment_changes;

    return sourceChanges
      .filter((change) => isEquipmentChangeVisibleForContexts(change, resolvedContextGroups))
      .map((change) => {
        const matchedContextGroup = findMatchingContextGroup(change, resolvedContextGroups);
        const resolvedEquipmentGroupId =
          change?.equipment_group_id || change?.suggested_equipment_group_id || undefined;
        const nextItemLabel =
          (resolvedEquipmentGroupId
            ? equipmentOptionsMap.get(resolvedEquipmentGroupId) || ""
            : "")
          || change?.item_label?.trim()
          || change?.equipment_title
          || "";

        return {
          ...change,
          event_name: change?.event_name || matchedContextGroup?.event_name || resolvedEventName || "",
          company_name: change?.company_name || matchedContextGroup?.company_name || resolvedCompanyName || "",
          stand_number: change?.stand_number || matchedContextGroup?.stand_number || primaryStandNumber || "",
          equipment_group_id: resolvedEquipmentGroupId,
          item_label: nextItemLabel,
        };
      })
      .filter((change) => change?.item_label?.trim());
  }, [
    currentStep,
    equipmentOptionsMap,
    form,
    resolvedCompanyName,
    resolvedContextGroups,
    resolvedEventName,
    primaryStandNumber,
    showModal,
    task.final_changes.equipment_changes,
    watchedEquipmentChanges,
  ]);

  const originalEmailEquipmentChanges = useMemo(
    () => [
      ...task.extracted_changes.equipment_changes.filter((change) =>
        isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)
      ),
      ...finalPreviewEquipmentChanges,
    ],
    [ finalPreviewEquipmentChanges, resolvedContextGroups, task.extracted_changes.equipment_changes ]
  );
  const originalEmailSections = useMemo(
    () => buildCommunicationOriginalEmailSections(task.original_body),
    [task.original_body]
  );
  const originalEmailSummary = useMemo(() => {
    const contentSections = originalEmailSections.filter((section) => section.type !== "header");
    const summarySource = contentSections.slice(0, 2).map((section) => section.text.trim()).filter(Boolean).join(" ");

    return summarySource || task.ai_summary || task.subject || "\u2014";
  }, [originalEmailSections, task.ai_summary, task.subject]);
  const dismissedEntrySummary = task.ai_summary || originalEmailSummary;

  const finalPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      received_at: task.received_at,
      changes: {
        equipment_changes: finalPreviewEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        manager_note: "",
      },
    }),
    [
      finalPreviewEquipmentChanges,
      resolvedCompanyName,
      resolvedEventName,
      primaryStandNumber,
      resolvedStandUpdates,
      task.company_name,
      task.event_name,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedPreviewSource = useMemo(
    () => ({
      event_name: resolvedEventName,
      company_name: resolvedCompanyName,
      stand_number: primaryStandNumber,
      received_at: task.received_at,
      changes: {
        ...task.extracted_changes,
        equipment_changes: task.extracted_changes.equipment_changes.filter((change) =>
          isEquipmentChangeVisibleForContexts(change, resolvedContextGroups)
        ),
      },
    }),
    [
      resolvedCompanyName,
      resolvedContextGroups,
      resolvedEventName,
      primaryStandNumber,
      task.company_name,
      task.event_name,
      task.extracted_changes,
      task.received_at,
      task.stand_number,
    ]
  );

  const extractedLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(extractedPreviewSource),
    [extractedPreviewSource]
  );

  const finalLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(finalPreviewSource),
    [finalPreviewSource]
  );
  const stepTwoStandGroupCount =
    requestGroupCount || normalizedStandNumbers.length || (primaryStandNumber ? 1 : 0);
  const parsedStandCount = requestGroupCount || normalizedStandNumbers.length || (primaryStandNumber ? 1 : 0);
  const shouldColorizeStandGroups = stepTwoStandGroupCount > 1;
  const standToneStyleMap = useMemo(() => {
    const uniqueStandNumbers = Array.from(
      new Set(
        [
          ...displayRequestGroups.map((group) => group.stand_number),
          ...editableChangeGroups.map((group) => group.standNumber || ""),
          ...normalizedStandNumbers,
          task.stand_number || "",
        ]
          .map((value) => (typeof value === "string" ? value.trim() : ""))
          .filter(Boolean)
      )
    );

    return new Map(
      uniqueStandNumbers.map((standNumber, index) => [ standNumber, buildStandToneStyle(index) ])
    );
  }, [ displayRequestGroups, editableChangeGroups, normalizedStandNumbers, task.stand_number ]);
  const getStandToneProps = (
    standNumber?: string | null,
    enabled = true
  ): {
    toneClassName: string;
    surfaceClassName: string;
    style?: React.CSSProperties;
  } => {
    const normalizedStandNumber = typeof standNumber === "string" ? standNumber.trim() : "";
    const style = normalizedStandNumber
      ? enabled
        ? standToneStyleMap.get(normalizedStandNumber)
        : buildNeutralStandToneStyle()
      : undefined;

    return {
      toneClassName: style ? "communication-task-form__stand-tone" : "",
      surfaceClassName: style ? "communication-task-form__stand-surface" : "",
      style,
    };
  };
  const unresolvedReviewCount = pendingEquipmentSuggestionsCount + unresolvedEquipmentSelectionCount;
  const totalClarificationCount = resolvedQuestions.length;
  const contextStandLabels = normalizedStandNumbers
    .map((standNumber) => formatCanonicalStandNumberLabel(standNumber))
    .filter((standNumber) => standNumber !== "\u2014");
  const standsSummaryLabel =
    contextStandLabels.length > 0
      ? contextStandLabels.join(" • ")
      : primaryStandNumber
        ? formatCanonicalStandNumberLabel(primaryStandNumber)
        : "\u2014";
  const isLastStep = currentStep === steps.length;
  const stepTwoSummaryItems = useMemo(
    () => [
      {
        key: "changes",
        labelKey: "COMMUNICATION.Step2Summary_ChangesLabel",
        value: reviewChangesCount,
        caption: getCountLabel(
          finalPreviewEquipmentChanges.length,
          "COMMUNICATION.RequestGroup_ItemSingle",
          "COMMUNICATION.RequestGroup_Items"
        ),
        tone: reviewChangesCount > 0 ? "warning" : "success",
      },
      {
        key: "clarifications",
        labelKey: "COMMUNICATION.Step2Summary_ClarificationsLabel",
        value: openQuestionsCount,
        caption:
          resolvedQuestions.length > 0
            ? getCountLabel(
              resolvedQuestions.length,
              "COMMUNICATION.RequestGroup_QuestionSingle",
              "COMMUNICATION.RequestGroup_Questions"
            )
            : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions"),
        tone: openQuestionsCount > 0 ? "warning" : "default",
      },
      {
        key: "stands",
        labelKey: "COMMUNICATION.Step2Summary_StandsLabel",
        value: stepTwoStandGroupCount,
        caption: getCountLabel(
          visibleRequestItemsCount,
          "COMMUNICATION.RequestGroup_ParsedItemSingle",
          "COMMUNICATION.RequestGroup_ParsedItems"
        ),
        tone: hasMultipleRequestGroups ? "info" : "default",
      },
    ],
    [
      finalPreviewEquipmentChanges.length,
      hasMultipleRequestGroups,
      openQuestionsCount,
      resolvedQuestions.length,
      reviewChangesCount,
      stepTwoStandGroupCount,
      visibleRequestItemsCount,
    ]
  );
  const stepOneSummaryItems = useMemo(
    () => [
      {
        key: "stands",
        labelKey: "COMMUNICATION.Step1Summary_StandsLabel",
        value: parsedStandCount,
        caption: getCountLabel(
          parsedStandCount,
          "COMMUNICATION.RequestGroup_GroupSingle",
          "COMMUNICATION.RequestGroup_Groups"
        ),
        tone: parsedStandCount > 1 ? "info" : "default",
      },
      {
        key: "confidence",
        labelKey: "COMMUNICATION.Step1Summary_ParserConfidenceLabel",
        value: `${confidenceValue}%`,
        caption: localizeByKeyStatic(
          confidenceTone === "high"
            ? "COMMUNICATION.Confidence_High"
            : confidenceTone === "medium"
              ? "COMMUNICATION.Confidence_Medium"
              : "COMMUNICATION.Confidence_Low"
        ),
        tone: confidenceTone === "high" ? "success" : confidenceTone === "medium" ? "warning" : "default",
      },
      {
        key: "unresolved",
        labelKey: "COMMUNICATION.Step1Summary_UnresolvedLabel",
        value: unresolvedReviewCount,
        caption:
          unresolvedReviewCount > 0
            ? localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
              count: unresolvedReviewCount,
            })
            : localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedClear"),
        tone: unresolvedReviewCount > 0 ? "warning" : "success",
      },
      {
        key: "clarifications",
        labelKey: "COMMUNICATION.Step1Summary_ClarificationsLabel",
        value: totalClarificationCount,
        caption:
          totalClarificationCount > 0
            ? getCountLabel(
              totalClarificationCount,
              "COMMUNICATION.RequestGroup_QuestionSingle",
              "COMMUNICATION.RequestGroup_Questions"
            )
            : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions"),
        tone: totalClarificationCount > 0 ? "warning" : "default",
      },
    ],
    [
      confidenceTone,
      confidenceValue,
      parsedStandCount,
      totalClarificationCount,
      unresolvedReviewCount,
    ]
  );
  const clarificationStandContextLabel = useMemo(() => {
    const formattedStandNumbers = normalizedStandNumbers
      .map((standNumber) => formatCanonicalStandNumberLabel(standNumber))
      .filter((standNumber) => standNumber !== "\u2014");

    if (formattedStandNumbers.length === 1) {
      return localizeByKeyStatic("COMMUNICATION.QuestionCard_RelatedStand", {
        stand: formattedStandNumbers[0],
      });
    }

    if (formattedStandNumbers.length > 1) {
      return localizeByKeyStatic("COMMUNICATION.QuestionCard_RelatedStands", {
        count: formattedStandNumbers.length,
      });
    }

    return "";
  }, [normalizedStandNumbers]);
  const hasReplyHandoffNote = Boolean(resolvedReplyHandoffNote?.trim());
  const shouldShowReplyHandoffNote = showReplyHandoffNote || totalClarificationCount > 0 || hasReplyHandoffNote;
  const needsCustomerFollowUp = totalClarificationCount > 0 || hasReplyHandoffNote;
  const nextActionLabelKey = needsCustomerFollowUp
    ? "COMMUNICATION.NextAction_FollowUpNeeded"
    : "COMMUNICATION.NextAction_NoFollowUpNeeded";
  const replyDraftRecipient = typeof task.sender_email === "string" ? task.sender_email.trim() : "";
  const senderDisplayLabel = replyDraftRecipient || task.sender || "";
  const replyDraftSubject = useMemo(() => {
    const normalizedSubject = task.subject?.trim() || "";

    if (!normalizedSubject) {
      return "";
    }

    return /^re:/iu.test(normalizedSubject) ? normalizedSubject : `Re: ${normalizedSubject}`;
  }, [task.subject]);
  const replyDraftRecipientLabel = replyDraftRecipient
    || localizeByKeyStatic("COMMUNICATION.ReplyDraft_RecipientMissing");
  const replyDraftMailtoHref = useMemo(() => {
    if (!replyDraftRecipient) {
      return "";
    }

    const params = new URLSearchParams();

    if (replyDraftSubject) {
      params.set("subject", replyDraftSubject);
    }

    if (resolvedReplyHandoffNote?.trim()) {
      params.set("body", resolvedReplyHandoffNote.trim());
    }

    const queryString = params.toString();

    return `mailto:${replyDraftRecipient}${queryString ? `?${queryString}` : ""}`;
  }, [replyDraftRecipient, replyDraftSubject, resolvedReplyHandoffNote]);
  const replyDraftTemplates = useMemo(
    () => [
      {
        key: "available" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateAvailable",
      },
      {
        key: "unavailable" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateUnavailable",
      },
      {
        key: "under_review" as const,
        labelKey: "COMMUNICATION.ReplyDraft_TemplateUnderReview",
      },
    ],
    []
  );

  const getStepBadgeLabel = (step: TCommunicationTaskStep): string =>
    localizeByKeyStatic(
      step.badgeTextKey,
      step.count > 0 ? { count: step.count } : undefined
    );

  const getStepTitle = (step: TCommunicationTaskStep): string =>
    stripStepTitleIndex(localizeByKeyStatic(step.titleKey));

  const getStepDescription = (step: TCommunicationTaskStep): string =>
    localizeByKeyStatic(step.descriptionKey);

  const renderStepIcon = (step: TCommunicationTaskStep) => {
    const StepIcon = stepIconMap[step.icon];

    return <StepIcon />;
  };

  const applyReplyDraftTemplate = (templateKey: TReplyDraftTemplateKey) => {
    const labelKeyMap: Record<TReplyDraftTemplateKey, string> = {
      available: "COMMUNICATION.ReplyDraft_TextAvailable",
      unavailable: "COMMUNICATION.ReplyDraft_TextUnavailable",
      under_review: "COMMUNICATION.ReplyDraft_TextUnderReview",
    };

    form.setFieldValue("reply_handoff_note", localizeByKeyStatic(labelKeyMap[templateKey]));
    setShowReplyHandoffNote(true);
  };

  const handleOpenReplyDraft = () => {
    if (!replyDraftMailtoHref || typeof window === "undefined") {
      return;
    }

    window.location.href = replyDraftMailtoHref;
  };

  const scrollToFirstFieldError = (error: TValidateFieldsError) => {
    const firstField = error?.errorFields?.[0];

    if (firstField?.name) {
      form.scrollToField(firstField.name, { block: "center" });
    }

    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      const drawerBody = document.querySelector(".communication-drawer .ant-drawer-body") as HTMLElement | null;
      const formRoot = document.querySelector(".communication-drawer .communication-task-form") as HTMLElement | null;
      const stickyShell =
        formRoot?.querySelector(".communication-task-form__sticky-shell") as HTMLElement | null;
      const firstErroredItem =
        formRoot?.querySelector(".ant-form-item-has-error") as HTMLElement | null;

      if (!drawerBody || !formRoot || !firstErroredItem) {
        return;
      }

      const stickyOffset = (stickyShell?.offsetHeight || 0) + 24;
      const drawerRect = drawerBody.getBoundingClientRect();
      const targetRect = firstErroredItem.getBoundingClientRect();
      const nextScrollTop =
        drawerBody.scrollTop + (targetRect.top - drawerRect.top) - stickyOffset;

      drawerBody.scrollTo({
        top: Math.max(nextScrollTop, 0),
        behavior: "smooth",
      });
    });
  };

  const waitForStepRender = () =>
    new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 0);
    });

  const backButtonLabelKey =
    currentStep === 2
      ? "COMMUNICATION.Button_BackToStep1"
      : "COMMUNICATION.Button_BackToStep2";

  const nextButtonLabelKey =
    currentStep === 1
      ? "COMMUNICATION.Button_ContinueToStep2"
      : "COMMUNICATION.Button_ContinueToStep3";

  const getStepTwoValidationFieldNames = (
    equipmentChanges: TCommunicationTaskFormValues["equipment_changes"]
  ): TValidationFieldName[] =>
    equipmentChanges.flatMap((change, index, changes) => {
      if (shouldIgnoreEquipmentChangeValidation(change, index, changes, hasExistingEquipmentChanges)) {
        return [];
      }

      return [
        [ "equipment_changes", index, "equipment_group_id" ],
        [ "equipment_changes", index, "qty" ],
      ];
    });

  const getQuestionValidationFieldNames = (): TValidationFieldName[] =>
    resolvedQuestions.map((_, index) => [ "questions", index, "text" ]);

  const getStepOneValidationFieldNames = (): TValidationFieldName[] => {
    const contextGroups = resolvedContextGroups.length > 0 ? resolvedContextGroups : [ {
      id: `context-${task.id}-0`,
      event_name: "",
      company_name: "",
      stand_number: "",
    } ];

    return contextGroups.flatMap((_, index) => ([
      [ "context_groups", index, "event_name" ],
      [ "context_groups", index, "company_name" ],
      [ "context_groups", index, "stand_number" ],
    ]));
  };

  const validateStepFields = async (
    stepId: number,
    fieldNames: TValidationFieldName[]
  ): Promise<boolean> => {
    if (!fieldNames.length) {
      return true;
    }

    if (currentStep !== stepId) {
      setCurrentStep(stepId);
      await waitForStepRender();
    }

    try {
      await form.validateFields(fieldNames);
      return true;
    } catch (error) {
      scrollToFirstFieldError(error as TValidateFieldsError);
      return false;
    }
  };

  const validateMatchingStep = async (): Promise<boolean> =>
    validateStepFields(1, getStepOneValidationFieldNames());

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const isStepOneValid = await validateMatchingStep();

      if (!isStepOneValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 2 ? 2 : step));
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      if (!isMatchComplete) {
        await validateMatchingStep();
        return;
      }

      const isStepTwoValid = await validateStepFields(
        2,
        [
          ...getStepTwoValidationFieldNames(resolvedEquipmentChanges),
          ...getQuestionValidationFieldNames(),
        ]
      );

      if (!isStepTwoValid) {
        return;
      }

      setMaxUnlockedStep((step) => (step < 3 ? 3 : step));
      setCurrentStep(3);
      return;
    }
  };

  const handleApproveClick = async () => {
    if (!isMatchComplete) {
      await validateMatchingStep();
      return;
    }

    if (hasStepTwoBlockingIssues) {
      await validateStepFields(2, getStepTwoValidationFieldNames(resolvedEquipmentChanges));
      return;
    }

    void form.validateFields().then((validatedValues) => {
      const rawValues = form.getFieldsValue(true) as Partial<TCommunicationTaskFormValues>;
      const nextValues = buildFormValueSnapshot(task, {
        ...rawValues,
        ...validatedValues,
      }, {
        event_name: resolvedEventName,
        company_name: resolvedCompanyName,
        stand_number: primaryStandNumber,
        stand_numbers: normalizedStandNumbers,
        context_groups: resolvedContextGroups,
        equipment_changes: resolvedEquipmentChanges,
        stand_updates: resolvedStandUpdates,
        questions: resolvedQuestions,
      });

      onApprove(task.id, nextValues);
      hasTriggeredDirtyStartRef.current = false;
      setIsDirty(false);
      onDirtyChange?.(false);
      action?.({ ignoreUnsavedChanges: true });
    }).catch((error: TValidateFieldsError) => {
      scrollToFirstFieldError(error);
    });
  };

  const handleDismissNotRelevant = () => {
    if (!onDismissNotRelevant) {
      return;
    }

    onDismissNotRelevant(task.id, dismissReason);
    hasTriggeredDirtyStartRef.current = false;
    setIsDirty(false);
    onDirtyChange?.(false);
    setIsDismissModalOpen(false);
    action?.({ ignoreUnsavedChanges: true });
  };

  const patchEquipmentChange = (
    index: number,
    patch: Partial<TCommunicationTaskFormValues["equipment_changes"][number]>
  ) => {
    const equipmentChanges = form.getFieldValue("equipment_changes") || [];
    const nextEquipmentChanges = equipmentChanges.map(
      (
        change: TCommunicationTaskFormValues["equipment_changes"][number],
        changeIndex: number
      ) => (changeIndex === index ? { ...change, ...patch } : change)
    );

    form.setFieldValue("equipment_changes", nextEquipmentChanges);
  };

  const handleQuestionStatusToggle = (index: number, checked: boolean) => {
    form.setFieldValue(
      [ "questions", index, "status" ],
      checked ? "reviewed" : "open"
    );
  };

  const syncEquipmentChangeLabel = (index: number, equipmentGroupId?: string) => {
    const nextLabel = equipmentGroupId ? equipmentOptionsMap.get(equipmentGroupId) || "" : "";
    patchEquipmentChange(index, { item_label: nextLabel });
  };

  const handleConfirmEquipmentSuggestion = (index: number) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const nextEquipmentGroupId =
      currentChange.equipment_group_id || currentChange.suggested_equipment_group_id;

    patchEquipmentChange(index, {
      equipment_group_id: nextEquipmentGroupId,
      suggestion_status: "confirmed",
    });
    syncEquipmentChangeLabel(index, nextEquipmentGroupId);
  };

  const handleChooseAnotherEquipment = (index: number) => {
    patchEquipmentChange(index, {
      equipment_group_id: undefined,
      item_label: "",
      suggestion_status: "changed",
    });
    setOpenEquipmentField(index);
  };

  const handleEquipmentSelectChange = (index: number, equipmentGroupId?: string) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

    patchEquipmentChange(index, {
      equipment_group_id: equipmentGroupId,
      suggestion_status:
        suggestionMeta
          ? equipmentGroupId && equipmentGroupId === suggestionMeta.suggestedGroupId
            ? "pending"
            : "changed"
          : currentChange.suggestion_status,
    });
    syncEquipmentChangeLabel(index, equipmentGroupId);

    if (equipmentGroupId) {
      setOpenEquipmentField(null);
    }
  };

  const renderLogisticsTable = (
    rows: ReturnType<typeof buildCommunicationLogisticsPreview>,
    emptyKey: string,
    productLabelKey = "COMMUNICATION.Preview_Product",
    options?: {
      excludedContextGroups?: TCommunicationContextGroup[];
    }
  ) => {
    if (!rows.length) {
      return (
        <div className="communication-task-form__preview-empty">
          <Localize>{emptyKey}</Localize>
        </div>
      );
    }

    const eventLabel = localizeByKeyStatic("COMMUNICATION.Filter_Event");
    const actionLabel = localizeByKeyStatic("FORMS.Input_Label_Action");
    const productLabel = localizeByKeyStatic(productLabelKey);
    const companyLabel = localizeByKeyStatic("FORMS.Input_Label_CompanyName");
    const standLabel = localizeByKeyStatic("FORMS.Input_Label_StandNumber");
    const quantityLabel = localizeByKeyStatic("COMMUNICATION.Preview_TotalQty");
    const dateLabel = localizeByKeyStatic("COMMUNICATION.Preview_EmailDate");
    const groupedRows = rows.reduce<Array<{
      key: string;
      stand_number: string;
      company_name: string;
      event_name: string;
      rows: typeof rows;
    }>>((groups, row) => {
      const groupKey = buildContextGroupLookupKey(row.event_name, row.company_name, row.stand_number);
      const existingGroup = groups.find((group) => group.key === groupKey);

      if (existingGroup) {
        existingGroup.rows.push(row);
        return groups;
      }

      groups.push({
        key: groupKey,
        stand_number: row.stand_number || "",
        company_name: row.company_name || "",
        event_name: row.event_name || "",
        rows: [ row ],
      });

      return groups;
    }, []);
    const shouldRenderGroupedRows = shouldColorizeStandGroups && groupedRows.length > 1;
    const excludedContextGroups = options?.excludedContextGroups || [];
    const excludedContextSummary = excludedContextGroups
      .map((group) => [ group.event_name, group.company_name, formatCanonicalStandNumberLabel(group.stand_number) ].filter(Boolean).join(" / "))
      .join(", ");

    const renderLogisticsRow = (row: typeof rows[number]) => {
      const standTone = getStandToneProps(row.stand_number, shouldColorizeStandGroups);

      return (
        <div key={row.id} className="communication-task-form__logistics-row">
        <span className="communication-task-form__logistics-cell" data-label={eventLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.event_name || "\u2014"}
          >
            {row.event_name || "\u2014"}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--action"
          data-label={actionLabel}
        >
          <span
            className={`communication-task-form__action-tag communication-task-form__action-tag--${row.action}`}
          >
            <Localize>{actionLabelKeyMap[row.action]}</Localize>
          </span>
        </span>
        <span className="communication-task-form__logistics-cell" data-label={productLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.product || "\u2014"}
          >
            {row.product || "\u2014"}
          </span>
        </span>
        <span className="communication-task-form__logistics-cell" data-label={companyLabel}>
          <span
            className="communication-task-form__logistics-cell-copy"
            title={row.company_name || "\u2014"}
          >
            {row.company_name || "\u2014"}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--stand"
          data-label={standLabel}
        >
          <span
            className={`communication-task-form__logistics-cell-copy communication-task-form__logistics-cell-copy--stand ${standTone.toneClassName}`.trim()}
            style={standTone.style}
            title={formatCanonicalStandNumberLabel(row.stand_number)}
          >
            {formatCanonicalStandNumberLabel(row.stand_number)}
          </span>
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--qty"
          data-label={quantityLabel}
        >
          {row.quantity}
        </span>
        <span
          className="communication-task-form__logistics-cell communication-task-form__logistics-cell--date"
          data-label={dateLabel}
        >
          {row.received_at}
        </span>
        </div>
      );
    };

    return (
      <div className="communication-task-form__logistics-table">
        <div className="communication-task-form__logistics-head">
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={eventLabel}
            >
              <Localize>COMMUNICATION.Filter_Event</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--action">
            <span
              className="communication-task-form__logistics-head-copy"
              title={actionLabel}
            >
              <Localize>FORMS.Input_Label_Action</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={productLabel}
            >
              <Localize>{productLabelKey}</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={companyLabel}
            >
              <Localize>FORMS.Input_Label_CompanyName</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--stand">
            <span
              className="communication-task-form__logistics-head-copy"
              title={standLabel}
            >
              <Localize>FORMS.Input_Label_StandNumber</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--qty">
            <span
              className="communication-task-form__logistics-head-copy"
              title={quantityLabel}
            >
              <Localize>COMMUNICATION.Preview_TotalQty</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--date">
            <span
              className="communication-task-form__logistics-head-copy"
              title={dateLabel}
            >
              <Localize>COMMUNICATION.Preview_EmailDate</Localize>
            </span>
          </strong>
        </div>

        {excludedContextGroups.length > 0 && (
          <div className="communication-task-form__logistics-notice">
            <span className="communication-task-form__logistics-notice-label">
              {excludedContextGroups.length === 1 ? "1 stand excluded by manager" : `${excludedContextGroups.length} stands excluded by manager`}
            </span>
            <span
              className="communication-task-form__logistics-notice-copy"
              title={excludedContextSummary}
            >
              {excludedContextSummary}
            </span>
          </div>
        )}

        {shouldRenderGroupedRows
          ? groupedRows.map((group) => {
            const standTone = getStandToneProps(group.stand_number, true);

            return (
            <React.Fragment key={group.key}>
              <div
                className={`communication-task-form__logistics-group-header ${standTone.surfaceClassName}`.trim()}
                style={standTone.style}
              >
                <div className="communication-task-form__logistics-group-copy">
                  <strong
                    className={`communication-task-form__logistics-group-title ${standTone.toneClassName}`.trim()}
                    style={standTone.style}
                  >
                    {formatStandGroupTitle(group.stand_number)}
                  </strong>
                  <span className="communication-task-form__logistics-group-subtitle">
                    {[
                      group.company_name || "\u2014",
                      group.event_name || "\u2014",
                    ].join(" / ")}
                  </span>
                </div>
                <span className="communication-task-form__logistics-group-count">
                  {getCountLabel(
                    group.rows.length,
                    "COMMUNICATION.RequestGroup_ItemSingle",
                    "COMMUNICATION.RequestGroup_Items"
                  )}
                </span>
              </div>
              {group.rows.map(renderLogisticsRow)}
            </React.Fragment>
          );
          })
          : rows.map(renderLogisticsRow)}
      </div>
    );
  };

  const renderRequestGroups = (groups: TCommunicationRequestGroup[]) => {
    if (!groups.length) {
      return (
        <div className="communication-task-form__preview-empty">
          <Localize>COMMUNICATION.Text_NoExtractedChanges</Localize>
        </div>
      );
    }

    return (
      <div className="communication-task-form__request-groups">
        {groups.map((group) => {
          const reviewRequests = group.requests.filter(
            (request) => request.status !== "unidentified" && request.action !== "question"
          );
          const unidentifiedRequests = group.requests.filter(
            (request) => request.status === "unidentified"
          );
          const questionRequests = group.requests.filter(
            (request) => request.status !== "unidentified" && request.action === "question"
          );
          const groupSummary = [
            group.change_count > 0
              ? getCountLabel(
                group.change_count,
                "COMMUNICATION.RequestGroup_ItemSingle",
                "COMMUNICATION.RequestGroup_Items"
              )
              : null,
            group.question_count > 0
              ? getCountLabel(
                group.question_count,
                "COMMUNICATION.RequestGroup_QuestionSingle",
                "COMMUNICATION.RequestGroup_Questions"
              )
              : null,
            group.unidentified_count > 0
              ? getCountLabel(
                group.unidentified_count,
                "COMMUNICATION.RequestGroup_UnidentifiedSingle",
                "COMMUNICATION.RequestGroup_Unidentified"
              )
              : null,
          ].filter((value): value is string => Boolean(value));

        const requestSections = [
            {
              key: "review",
              titleKey: "COMMUNICATION.RequestGroup_SectionReview",
              hintKey: "COMMUNICATION.RequestGroup_SectionReviewHint",
              tone: "review",
              requests: reviewRequests,
            },
            {
              key: "unidentified",
              titleKey: "COMMUNICATION.RequestGroup_SectionUnidentified",
              hintKey: "COMMUNICATION.RequestGroup_SectionUnidentifiedHint",
              tone: "warning",
              requests: unidentifiedRequests,
            },
            {
              key: "questions",
              titleKey: "COMMUNICATION.RequestGroup_SectionQuestions",
              hintKey: "COMMUNICATION.RequestGroup_SectionQuestionsHint",
              tone: "question",
              requests: questionRequests,
            },
          ].filter((section) => section.requests.length > 0);
          const standTone = getStandToneProps(group.stand_number, shouldColorizeStandGroups);

          return (
            <div
              key={group.id}
              className={`communication-task-form__request-group ${standTone.surfaceClassName}`.trim()}
              style={standTone.style}
            >
              <div className="communication-task-form__request-group-header">
                <div className="communication-task-form__request-group-copy">
                  <strong
                    className={`communication-task-form__request-group-title ${standTone.toneClassName}`.trim()}
                    style={standTone.style}
                  >
                    {formatStandGroupTitle(group.stand_number)}
                  </strong>
                  <span className="communication-task-form__request-group-subtitle">
                    {[
                      group.company_name || localizeByKeyStatic("COMMUNICATION.RequestGroup_CompanyFallback"),
                      group.event_name || localizeByKeyStatic("COMMUNICATION.RequestGroup_EventFallback"),
                    ].join(" / ")}
                  </span>
                </div>
                <div className="communication-task-form__request-group-badges">
                  {groupSummary.map((summary) => (
                    <span
                      key={`${group.id}-${summary}`}
                      className="communication-task-form__request-group-badge"
                    >
                      {summary}
                    </span>
                  ))}
                </div>
              </div>

              <div className="communication-task-form__request-group-list">
                {requestSections.map((section) => (
                  <div
                    key={`${group.id}-${section.key}`}
                    className={`communication-task-form__request-section communication-task-form__request-section--${section.tone}`}
                  >
                    <div className="communication-task-form__request-section-header">
                      <div className="communication-task-form__request-section-copy">
                        <strong className="communication-task-form__request-section-title">
                          <Localize>{section.titleKey}</Localize>
                        </strong>
                        <small className="communication-task-form__request-section-hint">
                          <Localize>{section.hintKey}</Localize>
                        </small>
                      </div>
                      <span className="communication-task-form__request-section-count">
                        {getCountLabel(
                          section.requests.length,
                          "COMMUNICATION.RequestGroup_ItemSingle",
                          "COMMUNICATION.RequestGroup_Items"
                        )}
                      </span>
                    </div>

                    <div className="communication-task-form__request-section-list">
                      {section.requests.map((request) => {
                        const statusTone = getRequestStatusTone(request.status);
                        const requestTitle = request.item_name || request.item_notes || "\u2014";

                        return (
                          <div key={request.id} className="communication-task-form__request-row">
                            <span
                              className={`communication-task-form__action-tag communication-task-form__action-tag--${request.action}`}
                            >
                              <Localize>{requestActionLabelKeyMap[request.action]}</Localize>
                            </span>

                            <div className="communication-task-form__request-row-copy">
                              <strong title={requestTitle}>{requestTitle}</strong>
                              {request.item_notes && request.item_notes !== request.item_name && (
                                <small title={request.item_notes}>
                                  {localizeByKeyStatic("COMMUNICATION.RequestGroup_ItemNotes", {
                                    note: request.item_notes,
                                  })}
                                </small>
                              )}
                            </div>

                            <div className="communication-task-form__request-row-side">
                              <span
                                className={`communication-task-form__request-status communication-task-form__request-status--${statusTone}`}
                              >
                                <Localize>{requestStatusLabelKeyMap[request.status]}</Localize>
                              </span>
                              <span className="communication-task-form__request-confidence">
                                {localizeByKeyStatic("COMMUNICATION.RequestGroup_Confidence", {
                                  count: request.confidence,
                                })}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderClarificationsPanel = () => (
    <Form.List name="questions">
      {(fields, { remove }) => (
        <div className="communication-task-form__questions-panel communication-task-form__questions-panel--compact communication-task-form__questions-panel--workflow">
          <div className="communication-task-form__questions-header">
            <div className="communication-task-form__questions-copy">
              <span className="communication-task-form__questions-eyebrow">
                <Localize>COMMUNICATION.Preview_QuestionsWorking</Localize>
              </span>
              <strong className="communication-task-form__questions-title">
                <Localize
                  questions={resolvedQuestions.length}
                  open={openQuestionsCount}
                >
                  COMMUNICATION.Text_QuestionsSummary
                </Localize>
              </strong>
              <div className="communication-task-form__questions-meta">
                {followUpStandNumber && (
                  <span className="communication-task-form__question-meta-chip">
                    {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                      stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                    })}
                  </span>
                )}
                <span
                  className={`communication-task-form__question-meta-chip${openQuestionsCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
                >
                  {resolvedQuestions.length > 0
                    ? localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                      open: openQuestionsCount,
                      questions: resolvedQuestions.length,
                    })
                    : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
                </span>
                {unresolvedReviewCount > 0 && (
                  <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                    {localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
                      count: unresolvedReviewCount,
                    })}
                  </span>
                )}
              </div>
            </div>

            <div className="communication-task-form__questions-actions">
              <span
                className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}
              >
                {fields.length > 0 ? (
                  <Localize
                    questions={resolvedQuestions.length}
                    open={openQuestionsCount}
                  >
                    COMMUNICATION.Text_QuestionsSummary
                  </Localize>
                ) : (
                  <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                )}
              </span>
            </div>
          </div>

          <div className="communication-task-form__question-list">
            {fields.length === 0 && (
              <div className="communication-task-form__questions-empty">
                <Localize>COMMUNICATION.Text_NoQuestions</Localize>
              </div>
            )}

            {fields.map((field, index) => {
              const currentQuestion = resolvedQuestions?.[field.name];
              const questionStatus = currentQuestion?.status || "open";

              return (
                <div
                  key={field.key}
                  className={`communication-task-form__question-card communication-task-form__question-card--${questionStatus}`}
                >
                  <div className="communication-task-form__question-card-header">
                    <div className="communication-task-form__question-card-copy">
                      <div className="communication-task-form__question-card-title-row">
                        <strong className="communication-task-form__question-card-title">
                          {`${localizeByKeyStatic("COMMUNICATION.Field_Clarification")} #${index + 1}`}
                        </strong>
                        <span
                          className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                        >
                          {questionStatusLabelMap[questionStatus]}
                        </span>
                      </div>
                      <div className="communication-task-form__question-card-meta">
                        {clarificationStandContextLabel && (
                          <span className="communication-task-form__question-meta-chip">
                            {clarificationStandContextLabel}
                          </span>
                        )}
                        <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                          <Localize>COMMUNICATION.QuestionCard_AffectsChanges</Localize>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="communication-task-form__question-row">
                    <span className="communication-task-form__question-label">
                      <span className="communication-task-form__question-label-required">*</span>
                      <Localize>COMMUNICATION.QuestionCard_RequestLabel</Localize>
                    </span>
                    <Form.Item
                      className="communication-task-form__question-main"
                      name={[field.name, "text"]}
                      rules={[
                        {
                          required: true,
                          message: localizeByKeyStatic("COMMUNICATION.Validation_Question"),
                        },
                      ]}
                    >
                      <TextArea rows={3} />
                    </Form.Item>

                    <div className="communication-task-form__question-side">
                      <div className="communication-task-form__question-side-status">
                        <span className="communication-task-form__question-side-label">
                          <Localize>COMMUNICATION.QuestionCard_StatusLabel</Localize>
                        </span>
                        <Form.Item
                          className="communication-task-form__question-card-status communication-task-form__question-card-status--stacked"
                          name={[field.name, "status"]}
                        >
                          <Select suffixIcon={<Icons.Arrow />} options={questionStatusOptions} />
                        </Form.Item>
                      </div>

                      <div className="communication-task-form__question-side-actions">
                        <Checkbox
                          className="communication-task-form__question-toggle communication-task-form__question-toggle--inline"
                          checked={questionStatus === "reviewed"}
                          onChange={(event) =>
                            handleQuestionStatusToggle(field.name, event.target.checked)
                          }
                        >
                          <Localize>COMMUNICATION.QuestionStatus_Reviewed</Localize>
                        </Checkbox>

                        <Button
                          type="text"
                          className="communication-task-form__question-delete communication-task-form__question-delete--link"
                          icon={<Icons.Delete />}
                          onClick={() => remove(field.name)}
                        >
                          <span className="ellipsis">
                            <Localize>GLOBAL.Button_Text_Remove</Localize>
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Form.List>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      className={`model-form communication-task-form communication-task-form--${presentationMode}`}
      initialValues={initialFormValues}
      onValuesChange={() => {
        const rawValues = form.getFieldsValue(true) as Partial<TCommunicationTaskFormValues>;
        const nextSnapshot = JSON.stringify(
          normalizeTaskFormSnapshot(task, rawValues, equipmentIdToGroupIdMap)
        );
        const nextIsDirty = nextSnapshot !== initialFormSnapshot;

        if (nextIsDirty !== isDirty) {
          setIsDirty(nextIsDirty);
          onDirtyChange?.(nextIsDirty);
        }

        if (nextIsDirty && !hasTriggeredDirtyStartRef.current) {
          hasTriggeredDirtyStartRef.current = true;
          onDirtyStart?.(task.id);
        }
      }}
    >
      <div className="communication-task-form__sticky-shell">
        <div className="communication-task-form__context-ribbon">
          <div className="communication-task-form__context-ribbon-primary">
            <div className="communication-task-form__context-card communication-task-form__context-card--subject">
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Subject</Localize>
              </span>
              <strong className="communication-task-form__context-card-title" title={task.subject}>
                {task.subject}
              </strong>
              <span className="communication-task-form__context-card-meta" title={resolvedEventName || task.event_name}>
                {resolvedEventName || task.event_name || "\u2014"}
              </span>
            </div>

            <div className="communication-task-form__context-card">
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Sender</Localize>
              </span>
              <strong className="communication-task-form__context-card-title" title={task.sender}>
                {senderDisplayLabel}
              </strong>
              <span className="communication-task-form__context-card-meta" title={resolvedCompanyName || task.company_name}>
                {resolvedCompanyName || task.company_name || "\u2014"}
              </span>
            </div>

            <div
              className={`communication-task-form__context-card communication-task-form__context-card--confidence communication-task-form__context-card--confidence-${confidenceTone}`}
            >
              <span className="communication-task-form__context-card-label">
                <Localize>FORMS.Input_Label_Confidence</Localize>
              </span>
              <div className="communication-task-form__confidence-value">
                <strong className="communication-task-form__confidence-percent">{`${confidenceValue}%`}</strong>
                <span className={`communication-task-form__confidence-chip communication-task-form__confidence-chip--${confidenceTone}`}>
                  <Localize>
                    {confidenceTone === "high"
                      ? "COMMUNICATION.Confidence_High"
                      : confidenceTone === "medium"
                        ? "COMMUNICATION.Confidence_Medium"
                        : "COMMUNICATION.Confidence_Low"}
                  </Localize>
                </span>
              </div>
              <span className="communication-task-form__confidence-track" aria-hidden={true}>
                <span
                  className={`communication-task-form__confidence-fill communication-task-form__confidence-fill--${confidenceTone}`}
                  style={{ width: `${confidenceValue}%` }}
                />
              </span>
            </div>
          </div>

          {shouldRenderSecondaryContextRibbon && (
            <div className="communication-task-form__context-ribbon-secondary">
              <span className="communication-task-form__context-chip communication-task-form__context-chip--state">
                <span className="communication-task-form__context-chip-label">
                  <Localize>COMMUNICATION.WorkState_Label</Localize>
                </span>
                <strong
                  className={`communication-task-form__work-state-badge communication-task-form__work-state-badge--${task.work_state.status}`}
                >
                  {workStateLabel}
                </strong>
              </span>

              <span className="communication-task-form__context-chip">
                <span className="communication-task-form__context-chip-label">
                  <Localize>FORMS.Input_Label_ReceivedAt</Localize>
                </span>
                <strong>{formatCommunicationDateTime(task.received_at)}</strong>
              </span>

              <span className="communication-task-form__context-chip">
                <span className="communication-task-form__context-chip-label">
                  <Localize>{contextStandLabels.length > 1 ? "COMMUNICATION.Field_StandNumbers" : "FORMS.Input_Label_StandNumber"}</Localize>
                </span>
                <strong title={standsSummaryLabel}>{standsSummaryLabel}</strong>
              </span>

              {isAiFallbackTask && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--ai">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Source_Label</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.Source_AiFallback</Localize>
                  </strong>
                </span>
              )}

              {totalClarificationCount > 0 && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Step2Summary_ClarificationsLabel</Localize>
                  </span>
                  <strong>
                    {localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                      open: openQuestionsCount,
                      questions: totalClarificationCount,
                    })}
                  </strong>
                </span>
              )}

              {(!isMatchComplete || unresolvedReviewCount > 0) && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--warning">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>
                      {isMatchComplete
                        ? "COMMUNICATION.Step2Summary_ChangesLabel"
                        : "COMMUNICATION.Info_NeedsMatch"}
                    </Localize>
                  </span>
                  <strong>
                    {matchingMissingFieldLabels.length > 0
                      ? matchingMissingFieldLabels.join(", ")
                      : localizeByKeyStatic(
                        isMatchComplete
                          ? "COMMUNICATION.StepStatus_ReviewCount"
                          : "COMMUNICATION.Step1Summary_UnresolvedHint",
                        {
                          count: unresolvedReviewCount,
                        }
                      )}
                  </strong>
                </span>
              )}

              {hasSpamSignal && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--spam">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Info_SpamSignal</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.NotRelevantReason_spam_marketing</Localize>
                  </strong>
                </span>
              )}

              {isDismissedHistoryEntry && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--dismissed">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Audit_Outcome</Localize>
                  </span>
                  <strong>
                    <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
                  </strong>
                </span>
              )}

              {isDismissedHistoryEntry && dismissedReasonLabel && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--dismissed">
                  <span className="communication-task-form__context-chip-label">
                    <Localize>COMMUNICATION.Audit_Reason</Localize>
                  </span>
                  <strong>{dismissedReasonLabel}</strong>
                </span>
              )}

              {workStateTouchedLabel && (
                <span className="communication-task-form__context-chip communication-task-form__context-chip--muted">
                  <strong>{workStateTouchedLabel}</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {shouldRenderDemoStrip && task.demo_badge_key && task.demo_note_key && (
          <div className="communication-task-form__demo-strip">
            <span className="communication-task-form__demo-strip-icon" aria-hidden={true}>
              <Icons.Information />
            </span>
            <div className="communication-task-form__demo-strip-copy">
              <strong><Localize>{task.demo_badge_key}</Localize></strong>
              <span><Localize>{task.demo_note_key}</Localize></span>
            </div>
          </div>
        )}

        <div className={`communication-task-form__reading-pane${isReadingPaneExpanded ? " is-expanded" : " is-collapsed"}`}>
          <div className="communication-task-form__reading-pane-header">
            <div className="communication-task-form__reading-pane-title">
              <span className="communication-task-form__reading-pane-icon" aria-hidden={true}>
                <Icons.Document />
              </span>
              <div className="communication-task-form__reading-pane-copy">
                <span className="communication-task-form__reading-pane-label">
                  <Localize>FORMS.Input_Label_OriginalEmail</Localize>
                </span>
                <strong>
                  <Localize>COMMUNICATION.Section_AIContextSecondary</Localize>
                </strong>
              </div>
            </div>

            <Button
              className="light-bg communication-task-form__reading-pane-toggle"
              icon={<Icons.Arrow />}
              onClick={() => setIsReadingPaneExpanded((current) => !current)}
            >
              <span className="ellipsis">
                <Localize>
                  {isReadingPaneExpanded
                    ? "COMMUNICATION.Button_CollapseEmail"
                    : "COMMUNICATION.Button_ExpandEmail"}
                </Localize>
              </span>
            </Button>
          </div>

          {isReadingPaneExpanded ? (
            <div className="communication-task-form__reading-pane-body">
                <CommunicationOriginalEmailField
                  originalBody={task.original_body}
                  equipmentChanges={originalEmailEquipmentChanges}
                  headerMeta={task.original_email_meta}
                  isExpanded={true}
                  maxHeightReductionPx={shouldAutoCondenseReadingPane && isReadingPaneScrollCondensed ? 200 : 0}
                />
            </div>
          ) : (
            <button
              type="button"
              className="communication-task-form__reading-pane-summary"
              onClick={() => setIsReadingPaneExpanded(true)}
            >
              <span className="communication-task-form__reading-pane-summary-text">
                {originalEmailSummary}
              </span>
            </button>
          )}
        </div>

        {!isDismissedHistoryEntry && (
          <div className="communication-task-form__steps-sticky">
            <div className="communication-task-form__steps">
              {steps.map((step) => (
                <button
                  key={step.id}
                  type="button"
                  className={`communication-task-form__step communication-task-form__step--${step.status} ${currentStep === step.id ? "is-current" : ""}`}
                  onClick={() => setCurrentStep(step.id)}
                  disabled={!step.isClickable}
                  aria-current={currentStep === step.id ? "step" : undefined}
                  aria-label={`${getStepTitle(step)}. ${getStepBadgeLabel(step)}`}
                >
                  <span className="communication-task-form__step-number">{step.id}</span>
                  <span
                    className={`communication-task-form__step-icon communication-task-form__step-icon--${step.status}`}
                    aria-hidden={true}
                  >
                    {renderStepIcon(step)}
                  </span>
                  <span className="communication-task-form__step-copy">
                  <span className="communication-task-form__step-head">
                    <span className="communication-task-form__step-text">{getStepTitle(step)}</span>
                    <span className={`communication-task-form__step-badge communication-task-form__step-badge--${step.status}`}>
                      {getStepBadgeLabel(step)}
                    </span>
                  </span>
                  {shouldShowStepDescriptions && (
                    <span className="communication-task-form__step-description">{getStepDescription(step)}</span>
                  )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary communication-task-form__preview-card--dismissed">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.NotRelevantSummary_Title</Localize>
              </span>
              <span className="communication-task-form__preview-count communication-task-form__preview-count--warning">
                <Localize>COMMUNICATION.Info_NotRelevant</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.NotRelevantSummary_Hint</Localize>
            </small>

            <div className="communication-task-form__dismissed-summary-meta">
              <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
              </span>
              {dismissedReasonLabel && (
                <span className="communication-task-form__question-meta-chip">
                  {`${localizeByKeyStatic("COMMUNICATION.Audit_Reason")}: ${dismissedReasonLabel}`}
                </span>
              )}
            </div>

            <div className="communication-task-form__dismissed-summary-grid">
              <div className="communication-task-form__preview-line">
                <strong><Localize>COMMUNICATION.Audit_Outcome</Localize>:</strong>{" "}
                <Localize>COMMUNICATION.AuditOutcome_Dismissed</Localize>
              </div>
              <div className="communication-task-form__preview-line communication-task-form__preview-line--multiline">
                <strong><Localize>FORMS.Input_Label_AISummary</Localize>:</strong>{" "}
                {dismissedEntrySummary}
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 3 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--final-primary">
          <div className="communication-task-form__preview-header">
            <div className="communication-task-form__preview-header-copy">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_LogisticsFinal</Localize>
              </span>
              {excludedContextGroups.length > 0 && (
                <span className="communication-task-form__preview-count communication-task-form__preview-count--warning">
                  {excludedContextGroups.length === 1 ? "1 stand excluded by manager" : `${excludedContextGroups.length} stands excluded by manager`}
                </span>
              )}
            </div>
            <span className="communication-task-form__preview-count">
              <Localize count={finalLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
            </span>
          </div>
          <small className="communication-task-form__preview-hint">
            <Localize>COMMUNICATION.Preview_LogisticsFinalHint</Localize>
          </small>

          {renderLogisticsTable(
            finalLogisticsPreview,
            "COMMUNICATION.Text_NoFinalChanges",
            "COMMUNICATION.Preview_Product",
            {
              excludedContextGroups,
            }
          )}
        </div>
      )}

      {currentStep === 1 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <Form.Item name="event_name" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="company_name" hidden={true}>
            <Input />
          </Form.Item>
          <Form.Item name="stand_number" hidden={true}>
            <Input />
          </Form.Item>

          {shouldRenderStepOneSummary && (
            <div className="communication-task-form__review-summary communication-task-form__review-summary--strip communication-task-form__review-summary--strip-four">
              {stepOneSummaryItems.map((item) => (
                <div
                  key={item.key}
                  className={`communication-task-form__review-summary-item communication-task-form__review-summary-item--${item.tone}`}
                >
                  <span className="communication-task-form__review-summary-label">
                    <Localize>{item.labelKey}</Localize>
                  </span>
                  <strong className="communication-task-form__review-summary-value">
                    {item.value}
                  </strong>
                  <small className="communication-task-form__review-summary-caption">
                    {item.caption}
                  </small>
                </div>
              ))}
            </div>
          )}

          <div className={`communication-task-form__step-one-layout${requestGroupCount === 0 ? " communication-task-form__step-one-layout--single" : ""}`}>
            <div className="communication-task-form__step-one-main">
              <div className="communication-task-form__matching-panel">
                <div className="communication-task-form__stand-list communication-task-form__stand-list--contexts">
                  <div className="communication-task-form__stand-list-header">
                    <div className="communication-task-form__stand-list-copy">
                      <strong className="communication-task-form__stand-list-title">
                        <Localize>COMMUNICATION.Section_RequestGroups</Localize>
                      </strong>
                      <small className="communication-task-form__stand-list-hint">
                        Confirm event, company, and stand for each detected context group.
                      </small>
                    </div>

                    <Button
                      className="light-bg communication-task-form__stand-add"
                      icon={<Icons.Plus />}
                      onClick={() => {
                        const nextContextGroups = [
                          ...resolvedContextGroups,
                          {
                            id: `context-${task.id}-${Date.now()}`,
                            event_name: "",
                            company_name: "",
                            stand_number: "",
                          },
                        ];

                        form.setFieldValue("context_groups", nextContextGroups);
                      }}
                    >
                      Add group
                    </Button>
                  </div>

                  <Form.List name="context_groups">
                    {(fields) => (
                      <div className="communication-task-form__stand-list-rows communication-task-form__stand-list-rows--contexts">
                        {fields.map((field) => (
                          <div key={field.key} className="communication-task-form__context-row">
                            <div className="communication-task-form__context-row-fields">
                              <Form.Item
                                name={[field.name, "id"]}
                                hidden={true}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "event_name"]}
                                label={<Localize>FORMS.Input_Label_EventName</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_EventName"),
                                }]}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "company_name"]}
                                label={<Localize>FORMS.Input_Label_CompanyName</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_CompanyName"),
                                }]}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "stand_number"]}
                                label={<Localize>FORMS.Input_Label_StandNumber</Localize>}
                                rules={[{
                                  required: true,
                                  whitespace: true,
                                  message: localizeByKeyStatic("FORM_RULES.Required_StandNumber"),
                                }]}
                              >
                                <Input placeholder={localizeByKeyStatic("FORMS.Input_Label_StandNumber")} />
                              </Form.Item>
                            </div>
                            <Button
                              className="communication-task-form__stand-remove"
                              icon={<Icons.Delete />}
                              disabled={fields.length <= 1}
                              onClick={() => {
                                const nextContextGroups = resolvedContextGroups.filter((_, index) => index !== field.name);
                                form.setFieldValue("context_groups", nextContextGroups);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.List>
                </div>

                <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__matching-grid">
                  <Form.Item name="organizer_id" label={<Localize>FORMS.Input_Label_Organizer</Localize>}>
                    <Select
                      allowClear
                      options={organizerOptions}
                      suffixIcon={<Icons.Arrow />}
                      placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                    />
                  </Form.Item>
                  <Form.Item
                    name="exhibition_type_id"
                    label={<Localize>FORMS.Input_Label_ExhibitionTypes</Localize>}
                  >
                    <Select
                      allowClear
                      options={exhibitionOptions}
                      suffixIcon={<Icons.Arrow />}
                      placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                    />
                  </Form.Item>
                </div>
              </div>

              {shouldRenderStepOneFollowUpStrip && (
                <div className="communication-task-form__followup-strip">
                  <div className="communication-task-form__followup-strip-copy">
                    <span className="communication-task-form__questions-eyebrow">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Label</Localize>
                    </span>
                    <strong className="communication-task-form__followup-strip-title">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Title</Localize>
                    </strong>
                    <span className="communication-task-form__followup-strip-hint">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Step2Hint</Localize>
                    </span>
                  </div>

                  <div className="communication-task-form__followup-strip-meta">
                    {followUpStandNumber && (
                      <span className="communication-task-form__question-meta-chip">
                        {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                          stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                        })}
                      </span>
                    )}
                    <span
                      className={`communication-task-form__question-meta-chip${totalClarificationCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
                    >
                      {totalClarificationCount > 0
                        ? localizeByKeyStatic("COMMUNICATION.Text_QuestionsSummary", {
                          open: openQuestionsCount,
                          questions: totalClarificationCount,
                        })
                        : localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
                    </span>
                    <span
                      className={`communication-task-form__question-meta-chip${unresolvedReviewCount > 0 ? " communication-task-form__question-meta-chip--warning" : " communication-task-form__question-meta-chip--muted"}`}
                    >
                      {unresolvedReviewCount > 0
                        ? localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedHint", {
                          count: unresolvedReviewCount,
                        })
                        : localizeByKeyStatic("COMMUNICATION.Step1Summary_UnresolvedClear")}
                    </span>
                    {(totalClarificationCount > 0 || unresolvedReviewCount > 0) && (
                      <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                        <Localize>COMMUNICATION.QuestionsWorkflow_AffectsChanges</Localize>
                      </span>
                    )}
                    <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--muted">
                      <Localize>COMMUNICATION.QuestionsWorkflow_Step2Continue</Localize>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {requestGroupCount > 0 && (
              <div className="communication-task-form__step-one-side">
                <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--context communication-task-form__preview-card--source">
                  <div className="communication-task-form__preview-header">
                    <span className="communication-task-form__preview-title">
                      <Localize>COMMUNICATION.Section_RequestGroups</Localize>
                    </span>
                    <span className="communication-task-form__preview-count">
                      {`${getCountLabel(
                        requestGroupCount,
                        "COMMUNICATION.RequestGroup_GroupSingle",
                        "COMMUNICATION.RequestGroup_Groups"
                      )} / ${getCountLabel(
                        visibleRequestItemsCount,
                        "COMMUNICATION.RequestGroup_ParsedItemSingle",
                        "COMMUNICATION.RequestGroup_ParsedItems"
                      )}`}
                    </span>
                  </div>
                  <small className="communication-task-form__preview-hint">
                    <Localize>{requestGroupsHintKey}</Localize>
                  </small>

                  {hasMultipleRequestGroups && (
                    <Alert
                      className="communication-task-form__preview-note"
                      type="info"
                      showIcon
                      message={<Localize>COMMUNICATION.Concept_MultiGroupHint</Localize>}
                    />
                  )}

                  {renderRequestGroups(displayRequestGroups)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {currentStep === 2 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section communication-task-form__section--workflow">
          <div className="communication-task-form__workflow-canvas communication-task-form__workflow-canvas--step-two">
          {shouldRenderStepTwoSummary && (
            <div className="communication-task-form__review-summary communication-task-form__review-summary--strip">
              {stepTwoSummaryItems.map((item) => (
                <div
                  key={item.key}
                  className={`communication-task-form__review-summary-item communication-task-form__review-summary-item--${item.tone}`}
                >
                  <span className="communication-task-form__review-summary-label">
                    <Localize>{item.labelKey}</Localize>
                  </span>
                  <strong className="communication-task-form__review-summary-value">
                    {item.value}
                  </strong>
                  <small className="communication-task-form__review-summary-caption">
                    {item.caption}
                  </small>
                </div>
              ))}
            </div>
          )}

          {shouldRenderExtractedLogisticsBand && (
            <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--source communication-task-form__preview-card--band communication-task-form__preview-card--source-band">
              <div className="communication-task-form__preview-header">
                <span className="communication-task-form__preview-title">
                  <Localize>COMMUNICATION.Preview_LogisticsExtracted</Localize>
                </span>
                <span className="communication-task-form__preview-count">
                  <Localize count={extractedLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
                </span>
              </div>
              <small className="communication-task-form__preview-hint">
                <Localize>{extractedChangesHintKey}</Localize>
              </small>

              {renderLogisticsTable(
                extractedLogisticsPreview,
                "COMMUNICATION.Text_NoExtractedChanges",
                "COMMUNICATION.Preview_ItemFromEmail"
              )}
            </div>
          )}

          <div className="communication-task-form__workflow-main">
            {!hasMultiStandEditing && (
              <div className="communication-task-form__workflow-tools">
                <Button
                  className="light-bg communication-task-form__workflow-tool-btn"
                  icon={<Icons.Note />}
                  disabled
                >
                  <span className="ellipsis">
                    <Localize>COMMUNICATION.Button_ViewStandDrawing</Localize>
                  </span>
                </Button>
                <small className="communication-task-form__workflow-tool-note">
                  <Localize>COMMUNICATION.CadPreview_Hint</Localize>
                </small>
              </div>
            )}

            {pendingEquipmentSuggestionsCount > 0 && (
              <Alert
                className="communication-task-form__suggestions-alert"
                showIcon
                type="warning"
                message={
                  <Localize count={pendingEquipmentSuggestionsCount}>
                    COMMUNICATION.EquipmentSuggestion_PendingSummary
                  </Localize>
                }
              />
            )}

            <Form.List name="equipment_changes">
            {(fields, { add, remove }) => (
              <>
                {(() => {
                  const fieldMap = new Map(fields.map((field) => [ Number(field.name), field ]));
                  const changeGroupsToRender = hasMultiStandEditing
                    ? editableChangeGroups
                    : [
                      {
                        key: "single-stand",
                        eventName: primaryContextGroup?.event_name || resolvedEventName || undefined,
                        companyName: primaryContextGroup?.company_name || resolvedCompanyName || undefined,
                        standNumber: primaryStandNumber || undefined,
                        itemIndexes: fields.map((field) => Number(field.name)),
                      },
                    ];

                  const renderEquipmentChangeField = (field: typeof fields[number]) => {
                    const currentChange = watchedEquipmentChanges?.[field.name] || {};
                    const equipmentLabel =
                      equipmentOptionsMap.get(getEffectiveEquipmentGroupId(currentChange) || "") || "";
                    const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

                    return (
                      <div
                        key={field.key}
                        className={`row-grid grid-gap-10 row-gap-0 communication-task-form__row communication-task-form__change-grid${hasMultiStandEditing ? " communication-task-form__change-grid--multi-stand" : ""}${suggestionMeta ? ` communication-task-form__change-grid--${suggestionMeta.status}` : ""}`}
                      >
                        <Form.Item
                          name={[field.name, "item_label"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "equipment_id"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "event_name"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "company_name"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "equipment_title"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "suggested_equipment_group_id"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "suggestion_status"]}
                          hidden={true}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "action"]}
                          label={<Localize>FORMS.Input_Label_Action</Localize>}
                        >
                          <Select suffixIcon={<Icons.Arrow />} options={actionOptions} />
                        </Form.Item>
                        {hasMultiStandEditing && (
                          <Form.Item
                            name={[field.name, "stand_number"]}
                            label={<Localize>FORMS.Input_Label_StandNumber</Localize>}
                            rules={[{
                              required: true,
                              message: localizeByKeyStatic("FORM_RULES.Required_StandNumber"),
                            }]}
                          >
                            <Select
                              suffixIcon={<Icons.Arrow />}
                              options={standSelectOptions}
                              placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                              onChange={(value) => {
                                const nextStandNumber = typeof value === "string" ? value : "";
                                const matchedContextGroup = resolvedContextGroups.find((group) =>
                                  group.stand_number.trim() === nextStandNumber.trim()
                                );

                                patchEquipmentChange(field.name, {
                                  stand_number: nextStandNumber,
                                  event_name: matchedContextGroup?.event_name || undefined,
                                  company_name: matchedContextGroup?.company_name || undefined,
                                });
                              }}
                            />
                          </Form.Item>
                        )}
                        <Form.Item
                          name={[field.name, "equipment_group_id"]}
                          label={
                            <div className="communication-task-form__equipment-label">
                              <span className="communication-task-form__equipment-label-text">
                                <Localize>FORMS.Input_Label_Equipment</Localize>
                              </span>

                              {suggestionMeta && (
                                <div
                                  className={`communication-task-form__equipment-suggestion communication-task-form__equipment-suggestion--${suggestionMeta.status}`}
                                >
                                  <span className="communication-task-form__equipment-suggestion-badge">
                                    <Localize>
                                      {suggestionMeta.status === "pending"
                                        ? "COMMUNICATION.EquipmentSuggestion_Badge"
                                        : suggestionMeta.status === "confirmed"
                                          ? "COMMUNICATION.EquipmentSuggestion_ConfirmedBadge"
                                          : "COMMUNICATION.EquipmentSuggestion_ChangedBadge"}
                                    </Localize>
                                  </span>

                                  {suggestionMeta.status === "pending" ? (
                                    <div className="communication-task-form__equipment-suggestion-actions">
                                      <Button
                                        type="link"
                                        icon={<Icons.Check />}
                                        onClick={() => handleConfirmEquipmentSuggestion(field.name)}
                                      >
                                        <Localize>GLOBAL.Button_Text_Confirm</Localize>
                                      </Button>
                                      <Button
                                        type="link"
                                        onClick={() => handleChooseAnotherEquipment(field.name)}
                                      >
                                        <Localize>COMMUNICATION.Button_ChooseAnother</Localize>
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="communication-task-form__equipment-suggestion-status">
                                      <Localize>{suggestionStatusLabelKeyMap[suggestionMeta.status]}</Localize>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          }
                          rules={[{
                            validator: (_, value) => {
                              const equipmentChanges =
                                (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
                              const currentEquipmentChange = equipmentChanges[field.name];

                              if (
                                shouldIgnoreEquipmentChangeValidation(
                                  currentEquipmentChange,
                                  field.name,
                                  equipmentChanges,
                                  hasExistingEquipmentChanges
                                )
                              ) {
                                return Promise.resolve();
                              }

                              if ((typeof value === "string" && value.trim()) || getEffectiveEquipmentGroupId(currentEquipmentChange)) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error(getEquipmentValidationMessage(currentEquipmentChange))
                              );
                            },
                          }]}
                        >
                          <Select
                            key={`${field.key}-${currentChange?.equipment_group_id || currentChange?.suggested_equipment_group_id || "empty"}-${mergedEquipmentOptions.length}`}
                            suffixIcon={<Icons.Arrow />}
                            allowClear
                            showSearch
                            open={openEquipmentField === field.name ? true : undefined}
                            options={equipmentSelectOptions}
                            optionLabelProp="label"
                            optionFilterProp="searchLabel"
                            popupClassName="communication-task-form__equipment-dropdown"
                            placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                            title={equipmentLabel.length > 40 ? equipmentLabel : undefined}
                            onChange={(value) => handleEquipmentSelectChange(field.name, value)}
                            onDropdownVisibleChange={(isOpen) =>
                              setOpenEquipmentField(isOpen ? field.name : null)
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "qty"]}
                          label={<Localize>FORMS.Input_Label_Amount</Localize>}
                          rules={[{ required: true, message: localizeByKeyStatic("FORM_RULES.Required_Amount") }]}
                        >
                          <InputNumber min={1} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "color"]}
                          label={<Localize>FORMS.Input_Label_Color</Localize>}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item label=" ">
                          <Button
                            icon={<Icons.Delete />}
                            onClick={() => {
                              setOpenEquipmentField(null);
                              remove(field.name);
                            }}
                          />
                        </Form.Item>
                      </div>
                    );
                  };

                  return (
                    <div className="communication-task-form__change-groups">
                      {changeGroupsToRender.map((group) => {
                        const groupFields = group.itemIndexes
                          .map((index) => fieldMap.get(index))
                          .filter(Boolean) as typeof fields;
                        const standTone = getStandToneProps(group.standNumber, shouldColorizeStandGroups);

                        if (!groupFields.length) {
                          return null;
                        }

                        return (
                          <div
                            key={group.key}
                            className={`communication-task-form__change-group ${standTone.surfaceClassName}`.trim()}
                            style={standTone.style}
                          >
                            {hasMultiStandEditing && (
                              <div className="communication-task-form__change-group-header">
                                <div className="communication-task-form__request-group-copy">
                                  <strong
                                    className={`communication-task-form__change-group-title ${standTone.toneClassName}`.trim()}
                                    style={standTone.style}
                                  >
                                    {formatStandGroupTitle(group.standNumber)}
                                  </strong>
                                  {(group.companyName || group.eventName) && (
                                    <span className="communication-task-form__request-group-subtitle">
                                      {[ group.companyName || "\u2014", group.eventName || "\u2014" ].join(" / ")}
                                    </span>
                                  )}
                                </div>
                                <div className="communication-task-form__change-group-actions">
                                  <Button
                                    className="light-bg communication-task-form__change-group-tool-btn"
                                    icon={<Icons.Note />}
                                    disabled
                                  >
                                    <span className="ellipsis">
                                      <Localize>COMMUNICATION.Button_ViewStandDrawing</Localize>
                                    </span>
                                  </Button>
                                  <span className="communication-task-form__change-group-count">
                                    {getCountLabel(
                                      groupFields.length,
                                      "COMMUNICATION.RequestGroup_ItemSingle",
                                      "COMMUNICATION.RequestGroup_Items"
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}

                            <div className="communication-task-form__change-group-list">
                              {groupFields.map((field) => renderEquipmentChangeField(field))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="communication-task-form__add-actions">
                  <div className="communication-task-form__add-actions-primary">
                    <Button
                      className="light-bg"
                      icon={<Icons.Plus />}
                      onClick={() =>
                        add(
                          createEmptyEquipmentChange(
                            task.id,
                            hasMultiStandEditing ? undefined : primaryStandNumber
                          )
                        )
                      }
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_AddChange</Localize>
                      </span>
                    </Button>
                  </div>

                  {/*
                  (!showStandUpdates || !showManagerNote) && (
                    <div className="communication-task-form__add-actions-secondary">
                      {!showStandUpdates && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowStandUpdates(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddStandUpdate</Localize>
                          </span>
                        </Button>
                      )}

                      {!showManagerNote && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowManagerNote(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddManagerNote</Localize>
                          </span>
                        </Button>
                      )}
                    </div>
                  )}
                  */}
                </div>
              </>
            )}
            </Form.List>

            {renderClarificationsPanel()}

            {(shouldShowReplyHandoffNote || hasReplyHandoffNote) ? (
              <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--handoff communication-task-form__reply-draft-card communication-task-form__reply-draft-card--workflow">
              <div className="communication-task-form__preview-header">
                <span className="communication-task-form__preview-title">
                  <Localize>COMMUNICATION.HandoffNote_Title</Localize>
                </span>
                <span className={`communication-task-form__preview-count${hasReplyHandoffNote ? "" : " communication-task-form__preview-count--muted"}`}>
                  <Localize>
                    {hasReplyHandoffNote
                      ? "COMMUNICATION.HandoffNote_StatusPrepared"
                      : "COMMUNICATION.HandoffNote_StatusEmpty"}
                  </Localize>
                </span>
              </div>

              <div className="communication-task-form__reply-draft-meta">
                {followUpStandNumber && (
                  <span className="communication-task-form__question-meta-chip">
                    {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                      stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                    })}
                  </span>
                )}
                <span className="communication-task-form__question-meta-chip">
                  <Localize>COMMUNICATION.QuestionCard_FollowUp</Localize>
                </span>
              </div>

              <div className="communication-task-form__reply-draft-addresses">
                <div className="communication-task-form__reply-draft-address">
                  <span className="communication-task-form__reply-draft-address-label">
                    <Localize>COMMUNICATION.ReplyDraft_To</Localize>
                  </span>
                  <strong
                    className="communication-task-form__reply-draft-address-value"
                    title={replyDraftRecipientLabel}
                  >
                    {replyDraftRecipientLabel}
                  </strong>
                </div>
                <div className="communication-task-form__reply-draft-address">
                  <span className="communication-task-form__reply-draft-address-label">
                    <Localize>COMMUNICATION.ReplyDraft_Subject</Localize>
                  </span>
                  <strong
                    className="communication-task-form__reply-draft-address-value"
                    title={replyDraftSubject || task.subject}
                  >
                    {replyDraftSubject || task.subject}
                  </strong>
                </div>
              </div>

              <div className="communication-task-form__reply-draft-templates">
                <span className="communication-task-form__reply-draft-template-label">
                  <Localize>COMMUNICATION.ReplyDraft_QuickReplies</Localize>
                </span>
                <div className="communication-task-form__reply-draft-template-list">
                  {replyDraftTemplates.map((template) => (
                    <Button
                      key={template.key}
                      className="light-bg communication-task-form__reply-draft-template"
                      onClick={() => applyReplyDraftTemplate(template.key)}
                    >
                      <span className="ellipsis">
                        <Localize>{template.labelKey}</Localize>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              <Form.Item
                name="reply_handoff_note"
                className="communication-task-form__handoff-note-field"
              >
                <TextArea
                  rows={3}
                  placeholder={localizeByKeyStatic("COMMUNICATION.HandoffNote_Placeholder")}
                />
              </Form.Item>

              <div className="communication-task-form__reply-draft-footer">
                <small className="communication-task-form__reply-draft-note">
                  <Localize>COMMUNICATION.ReplyDraft_OpenMailAppHint</Localize>
                </small>

                <div className="communication-task-form__reply-draft-actions">
                  <Button
                    type="primary"
                    className="communication-task-form__reply-draft-open"
                    disabled={!replyDraftRecipient}
                    onClick={handleOpenReplyDraft}
                  >
                    <span className="ellipsis">
                      <Localize>COMMUNICATION.ReplyDraft_OpenMailApp</Localize>
                    </span>
                  </Button>
                </div>
                </div>
              </div>
            ) : (
              <div className="communication-task-form__handoff-note-add">
                <Button
                  className="light-bg"
                  icon={<Icons.Plus />}
                  onClick={() => setShowReplyHandoffNote(true)}
                >
                  <span className="ellipsis">
                    <Localize>COMMUNICATION.HandoffNote_Add</Localize>
                  </span>
                </Button>
              </div>
            )}

            {showStandUpdates && (
              <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__flooring">
              <Form.Item
                name={["stand_updates", "flooring_id"]}
                label={<Localize>FORMS.Input_Label_Flooring</Localize>}
              >
                <Select
                  suffixIcon={<Icons.Arrow />}
                  allowClear
                  options={flooringOptions}
                  placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                />
              </Form.Item>
              <Form.Item
                name={["stand_updates", "flooring_color"]}
                label={<Localize>FORMS.Input_Label_CarpetColor</Localize>}
              >
                <Input />
              </Form.Item>
            </div>
            )}

            {showManagerNote && (
              <Form.Item name="manager_note" label={<Localize>FORMS.Input_Label_ManagerNote</Localize>}>
                <TextArea rows={4} />
              </Form.Item>
            )}
          </div>
          </div>
        </div>
      )}

      {currentStep === 3 && !isDismissedHistoryEntry && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_QuestionsResult</Localize>
              </span>
              <span className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}>
                {resolvedQuestions.length > 0 ? (
                  <Localize
                    questions={resolvedQuestions.length}
                    open={openQuestionsCount}
                  >
                    COMMUNICATION.Text_QuestionsSummary
                  </Localize>
                ) : (
                  <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                )}
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize
                reviewed={reviewedQuestionsCount}
                questions={resolvedQuestions.length}
              >
                COMMUNICATION.Preview_QuestionsResultHint
              </Localize>
            </small>

            {resolvedQuestions.length > 0 ? (
              <div className="communication-task-form__question-result-list">
                {resolvedQuestions.map((question, index) => {
                  const questionStatus = question?.status || "open";

                  return (
                    <div
                      key={question?.id || `question-result-${index}`}
                      className={`communication-task-form__question-result communication-task-form__question-result--${questionStatus}`}
                    >
                      <div className="communication-task-form__question-result-header">
                        <strong className="communication-task-form__question-result-title">
                          {`${localizeByKeyStatic("COMMUNICATION.Field_Clarification")} #${index + 1}`}
                        </strong>
                        <span
                          className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                        >
                          {questionStatusLabelMap[questionStatus]}
                        </span>
                      </div>
                      <div className="communication-task-form__question-result-text">
                        {question?.text?.trim() || localizeByKeyStatic("COMMUNICATION.Text_NoQuestions")}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="communication-task-form__questions-empty">
                <Localize>COMMUNICATION.Text_NoQuestions</Localize>
              </div>
            )}
          </div>

          <div className="communication-task-form__preview-card communication-task-form__preview-card--full communication-task-form__preview-card--secondary communication-task-form__preview-card--final-followup">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.FinalFollowUp_Title</Localize>
              </span>
              <span className={`communication-task-form__preview-count communication-task-form__preview-count--${needsCustomerFollowUp ? "warning" : "success"}`}>
                <Localize>{nextActionLabelKey}</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.FinalFollowUp_Hint</Localize>
            </small>

            <div className="communication-task-form__reply-draft-addresses communication-task-form__reply-draft-addresses--summary">
              <div className="communication-task-form__reply-draft-address">
                <span className="communication-task-form__reply-draft-address-label">
                  <Localize>COMMUNICATION.ReplyDraft_To</Localize>
                </span>
                <strong
                  className="communication-task-form__reply-draft-address-value"
                  title={replyDraftRecipientLabel}
                >
                  {replyDraftRecipientLabel}
                </strong>
              </div>
              <div className="communication-task-form__reply-draft-address">
                <span className="communication-task-form__reply-draft-address-label">
                  <Localize>COMMUNICATION.ReplyDraft_Subject</Localize>
                </span>
                <strong
                  className="communication-task-form__reply-draft-address-value"
                  title={replyDraftSubject || task.subject}
                >
                  {replyDraftSubject || task.subject}
                </strong>
              </div>
            </div>

            {hasReplyHandoffNote ? (
              <div className="communication-task-form__preview-line communication-task-form__preview-line--multiline">
                {resolvedReplyHandoffNote}
              </div>
            ) : (
              <div className="communication-task-form__preview-empty">
                <Localize>COMMUNICATION.HandoffNote_EmptyPreview</Localize>
              </div>
            )}

            <div className="communication-task-form__next-action-meta">
              <span className={`communication-task-form__question-meta-chip${hasReplyHandoffNote ? "" : " communication-task-form__question-meta-chip--muted"}`}>
                <Localize>
                  {hasReplyHandoffNote
                    ? "COMMUNICATION.HandoffNote_StatusPrepared"
                    : "COMMUNICATION.HandoffNote_StatusEmpty"}
                </Localize>
              </span>
              {followUpStandNumber && needsCustomerFollowUp && (
                <span className="communication-task-form__question-meta-chip">
                  {localizeByKeyStatic("COMMUNICATION.QuestionsWorkflow_RelatedStand", {
                    stand: formatCanonicalStandNumberLabel(followUpStandNumber),
                  })}
                </span>
              )}
              {needsCustomerFollowUp && (
                <span className="communication-task-form__question-meta-chip communication-task-form__question-meta-chip--warning">
                  <Localize>COMMUNICATION.QuestionsWorkflow_ClarificationNeeded</Localize>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {auditEntry && (
        <div className="communication-task-form__section">
          <h4><Localize>COMMUNICATION.Section_Audit</Localize></h4>
          <div className="communication-audit-box">
            <div>
              <strong><Localize>COMMUNICATION.Audit_Outcome</Localize>:</strong>{" "}
              <Localize>
                {auditEntry.outcome === "dismissed"
                  ? "COMMUNICATION.AuditOutcome_Dismissed"
                  : "COMMUNICATION.AuditOutcome_Approved"}
              </Localize>
            </div>
            <div>
              <strong><Localize>COMMUNICATION.Table_ProcessedBy</Localize>:</strong> {auditEntry.processed_by}
            </div>
            <div>
              <strong><Localize>COMMUNICATION.Table_ProcessedAt</Localize>:</strong>{" "}
              {formatCommunicationDateTime(auditEntry.processed_at)}
            </div>
            {auditEntry.outcome === "dismissed" && auditEntry.dismissed_reason && (
              <div>
                <strong><Localize>COMMUNICATION.Audit_Reason</Localize>:</strong>{" "}
                {localizeByKeyStatic(`COMMUNICATION.NotRelevantReason_${auditEntry.dismissed_reason}`)}
              </div>
            )}
            {auditEntry.excluded_context_groups && auditEntry.excluded_context_groups.length > 0 && (
              <div>
                <strong>Excluded context groups:</strong>{" "}
                {auditEntry.excluded_context_groups
                  .map((group) => [ group.event_name, group.company_name, group.stand_number ].filter(Boolean).join(" / "))
                  .join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-btn-holder communication-task-form__actions">
        <div className="communication-task-form__actions-start">
          {shouldShowMarkAsNotRelevant && (
            <Button
              className="communication-task-form__dismiss-btn"
              onClick={() => setIsDismissModalOpen(true)}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_MarkNotRelevant</Localize>
              </span>
            </Button>
          )}

          <Button className="light-bg" onClick={() => action?.()}>
            <span className="ellipsis">
              <Localize>GLOBAL.Button_Text_Cancel</Localize>
            </span>
          </Button>

          {!isDismissedHistoryEntry && currentStep > 1 && (
            <Button
              className="light-bg"
              onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
            >
              <span className="ellipsis">
                <span className="communication-task-form__nav-arrow" aria-hidden={true}>{"\u2190"}</span>
                {localizeByKeyStatic(backButtonLabelKey)}
              </span>
            </Button>
          )}
        </div>

        <div className="communication-task-form__actions-end">
          {!isDismissedHistoryEntry && !isLastStep && (
            <Button
              type="primary"
              onClick={handleNextStep}
            >
              <span className="ellipsis">
                {localizeByKeyStatic(nextButtonLabelKey)}
                <span className="communication-task-form__nav-arrow" aria-hidden={true}>{"\u2192"}</span>
              </span>
            </Button>
          )}

          {!isDismissedHistoryEntry && isLastStep && canApprove && !isAuditView && (
            <Button
              type="primary"
              className="btn-right"
              icon={<Icons.Check />}
              onClick={handleApproveClick}
            >
              <span className="ellipsis">
                <Localize>GLOBAL.Button_Text_Approve</Localize>
              </span>
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={isDismissModalOpen}
        onCancel={() => setIsDismissModalOpen(false)}
        onOk={handleDismissNotRelevant}
        okText={localizeByKeyStatic("COMMUNICATION.NotRelevant_Confirm")}
        okButtonProps={{ className: "communication-task-form__dismiss-confirm-btn" }}
        cancelText={localizeByKeyStatic("GLOBAL.Button_Text_Cancel")}
        cancelButtonProps={{ className: "communication-task-form__dismiss-cancel-btn" }}
        title={localizeByKeyStatic("COMMUNICATION.NotRelevant_Title")}
        wrapClassName="communication-task-form__dismiss-modal-wrap"
      >
        <div className="communication-task-form__dismiss-modal">
          <p className="communication-task-form__dismiss-hint">
            {localizeByKeyStatic(
              hasDismissReviewSignals
                ? "COMMUNICATION.NotRelevant_HintWithExtractedData"
                : "COMMUNICATION.NotRelevant_Hint"
            )}
          </p>

          <Form.Item
            label={localizeByKeyStatic("COMMUNICATION.NotRelevant_ReasonLabel")}
            className="communication-task-form__dismiss-field"
          >
            <Select
              value={dismissReason}
              options={dismissReasonOptions}
              suffixIcon={<Icons.Arrow />}
              onChange={(value) => setDismissReason(value as TCommunicationDismissReason)}
            />
          </Form.Item>
        </div>
      </Modal>
    </Form>
  );
};

export default CommunicationTaskForm;

